// ---------------------------------------------------------------------------
// v2 API client — User management, enhanced projects, agent registry
// ---------------------------------------------------------------------------

import { clearAuth, type AuthUser } from "./auth";

const BASE = "/api/v2";

// Client-side timeout for the BODS/Informatica legacy-import batch parse
// requests -- both are single synchronous fetches (no async-job-plus-
// polling pattern yet), so this is the only backstop against an
// indefinitely spinning UI if the server hangs on a large/pathological
// upload.
const LEGACY_IMPORT_TIMEOUT_MS = 120_000;

// Methods the backend's CSRF middleware requires a double-submit token for
// (mirrors SAFE_METHODS in ztd_app/api/core/middleware/csrf.py — GET/HEAD/
// OPTIONS are exempt there, everything else needs X-CSRF-Token).
const CSRF_REQUIRED_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

// Normalizes a backend error `detail` into a display string. FastAPI returns
// `detail` as a plain string for HTTPException, but as a list of
// `{loc, msg, type}` objects for pydantic validation errors (422) -- passing
// that array straight into `new Error(...)` stringifies it via Array.toString
// as "[object Object],[object Object],...", which is what a user actually
// saw on screen instead of a real message.
// Attach a JSON payload as a file part rather than a plain form field: the
// backend (Starlette) caps a non-file multipart field at 1MB, which a scanned
// schema or a large STTM's mappings exceed.
export function appendJsonFile(
  formData: FormData,
  name: string,
  value: unknown,
): void {
  formData.append(
    name,
    new Blob([JSON.stringify(value)], { type: "application/json" }),
    `${name}.json`,
  );
}

export function formatApiErrorDetail(
  detail: unknown,
  fallback: string,
): string {
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((e) => {
        if (e && typeof e === "object" && "msg" in e) {
          const loc = Array.isArray((e as { loc?: unknown[] }).loc)
            ? (e as { loc: unknown[] }).loc
                .filter((p) => p !== "body")
                .join(".")
            : "";
          const msg = String((e as { msg: unknown }).msg);
          return loc ? `${loc}: ${msg}` : msg;
        }
        return typeof e === "string" ? e : JSON.stringify(e);
      })
      .join("; ");
  }
  if (detail && !Array.isArray(detail) && typeof detail === "object") {
    return JSON.stringify(detail);
  }
  return fallback;
}

/** Thrown by `request()` on a non-2xx response. Carries the parsed response
 * body (RFC 7807 ProblemDetail, possibly with extra fields like
 * `existing_project`) so callers that need more than the display string can
 * get at it — most callers just read `.message` like a plain Error. */
export class ApiError extends Error {
  body: unknown;
  constructor(message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.body = body;
  }
}

/** Standard JSON request — auth cookie is sent automatically via credentials:'include'. */
async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(CSRF_REQUIRED_METHODS.has(method)
      ? {
          "X-CSRF-Token":
            document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
        }
      : {}),
    ...(init?.headers as Record<string, string>),
  };

  const res = await fetch(`${BASE}${url}`, {
    ...init,
    headers,
    credentials: "include",
  });

  // A 401 from /auth/login means bad credentials, not an expired session.
  // Let it fall through to detail-parsing so the backend's "Invalid email or
  // password" message reaches the UI. For any other endpoint, a 401 means the
  // session token is gone/expired mid-session — clear auth and surface that.
  if (res.status === 401 && url !== "/auth/login") {
    clearAuth();
    throw new Error("Session expired — please log in again.");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new ApiError(
      formatApiErrorDetail(body.detail, `Request failed: ${res.status}`),
      body,
    );
  }
  // 204 No Content
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

// ---------------------------------------------------------------------------
// Types — mirrors backend schemas
// ---------------------------------------------------------------------------
// Historically a 3-value union (admin/project_user/general_user). The
// backend now returns the real RBAC role name (e.g. platform_admin,
// conference_attendee, data_engineer, ...) verbatim, so this is a plain
// string — see UserManagement.tsx's ROLE_LABELS for the display mapping.
export type UserRole = string;
export type ProjectMemberRole = "owner" | "member" | "reviewer";
export type RunStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "approved"
  | "rejected";

export interface UserOut {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  /** Only populated by the /auth/* endpoints (login/me/refresh); absent
   * from the admin users-list shape, which doesn't need per-user
   * permission codenames for its own UI. */
  permissions?: string[];
  is_active: boolean;
  must_change_password: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectMemberOut {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  role: ProjectMemberRole;
  /** DQ-rule role on this project (DQ rule governance): the only source of rule powers besides platform admin. */
  rule_role?: "dq_analyst" | "data_steward" | null;
  created_at: string;
}

export interface ProjectAgentOut {
  agent_id: string;
  enabled: boolean;
  config: string;
}

export interface LLMConfigOut {
  id: string;
  provider: string;
  model_name: string;
  has_api_key: boolean;
  temperature: number;
  max_tokens: number;
  additional_config: string;
  updated_at: string;
}

export interface GlobalLLMConfigOut {
  id: string;
  provider: string;
  model_name: string;
  has_api_key: boolean;
  temperature: number;
  max_tokens: number;
  additional_config: string;
  updated_at: string;
}

export interface LLMProviderInfo {
  id: string;
  label: string;
  models: { id: string; label: string }[];
}

export type ExecutionMode = "autonomous" | "direct";
export type OrchestratorStepStatus =
  | "pending"
  | "waiting_input"
  | "running"
  | "waiting_approval"
  // `approved` / `rejected` are the terminal HITL-gate decisions the backend
  // writes when a conditional step's gate is decided via /step/{id}/approve
  // or /reject (see orchestrator_v2_router._transition_step).
  | "approved"
  | "rejected"
  | "completed"
  | "skipped"
  | "failed";

export interface ProjectOut {
  id: string;
  name: string;
  description: string;
  requirements: string;
  source_data_info: string;
  domain: string;
  tech_stack: string;
  execution_mode: ExecutionMode;
  goal: string;
  created_by: string;
  created_by_name: string;
  members: ProjectMemberOut[];
  agents: ProjectAgentOut[];
  llm_config: LLMConfigOut | null;
  source_db_config: DBConfigOut | null;
  target_db_config: DBConfigOut | null;
  created_at: string;
  updated_at: string;
}

export interface DBConfigOut {
  id: string;
  db_type: string;
  host: string;
  port: number | null;
  database: string;
  schema_name: string;
  username: string;
  has_password: boolean;
  auth_type: string; // 'password' | 'pat'
  account: string;
  warehouse: string;
  role: string;
  service_name: string; // Oracle TNS service name
  http_path: string; // Databricks SQL warehouse HTTP path
  client_id: string; // Databricks OAuth machine-to-machine
  has_client_secret: boolean; // mirrors has_password — never round-tripped in plaintext
  jdbc_driver_class: string;
  jdbc_url: string;
  jdbc_driver_jar_path: string;
  bridge_label: string;
  copybook_text: string;
  additional_config: string;
  updated_at: string;
  /** Only present on the multi-connection list/create endpoints below. */
  name?: string;
}

// Mirrors ztd_sdk.connectors.ConnectorField/ConnectorDef — the shared
// registry every agent's "which db types can I connect to" list now comes
// from, instead of a hand-typed array per component (the root cause of two
// real bugs: Data Converter's dropdown missing Snowflake, the Profiler's
// dropdown advertising MariaDB/Redshift with zero backend support).
export interface ConnectorFieldOut {
  key: string;
  label: string;
  required: boolean;
  secret: boolean;
  default: string | null;
  help_text: string | null;
}

export interface ConnectorOut {
  db_type: string;
  label: string;
  connection_kind: "network" | "file_path" | "bridge" | "file";
  default_port: number | null;
  default_schema: string | null;
  core_fields: string[];
  extra_fields: ConnectorFieldOut[];
  notes: string;
}

// Shared PUT payload shape for source-db/target-db/test-db-connection —
// additive fields (service_name/http_path/client_id/client_secret for
// Oracle/Databricks, jdbc_*/bridge_label for the mainframe JDBC bridge,
// copybook_text for the mainframe flat-file kind) alongside the original
// postgres/mysql/mssql/oracle/snowflake fields every other project_db_config
// consumer (Data Profiler, Data Guardian, etc.) already sends.
export interface ProjectDbConfigInput {
  db_type: string;
  host?: string;
  port?: number;
  database?: string;
  schema_name?: string;
  username?: string;
  password?: string;
  auth_type?: string;
  account?: string;
  warehouse?: string;
  role?: string;
  service_name?: string;
  http_path?: string;
  client_id?: string;
  client_secret?: string;
  jdbc_driver_class?: string;
  jdbc_url?: string;
  jdbc_driver_jar_path?: string;
  bridge_label?: string;
  copybook_text?: string;
  additional_config?: Record<string, unknown>;
}

// Engagement KB binding for a project. `dq_rule_packs` is an optional
// narrowing of which `dq-rules.*` packs the profiler loads; [] means all.
export interface EngagementConfigOut {
  engagement_slug: string;
  dq_rule_packs: string[];
}

// One engagement KB available on disk + its dq-rules.* pack names.
export interface EngagementInfo {
  slug: string;
  packs: string[];
}

// Mirrors the backend contract emitted by
// orchestrator_v2_router._step_to_dict — keep these in lockstep.
export interface OrchestratorStepOut {
  id: string;
  step_order: number;
  agent_id: string;
  title: string;
  description: string;
  status: OrchestratorStepStatus;
  question: string;
  user_response: string;
  output_summary: string;
  output_artifacts: Record<string, unknown>;
  input_config: Record<string, unknown>;
  is_conditional: boolean;
  // Set when a step dispatched an agent run (e.g. profiler session id).
  dispatched_session_id: string | null;
  // Set on a conditional step once its HITL gate is created on /run; the gate
  // is decided via /step/{id}/approve|reject.
  hitl_gate_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrchestratorPlanOut {
  id: string;
  project_id: string;
  goal: string;
  status: string;
  current_step_index: number;
  steps: OrchestratorStepOut[];
  created_at: string;
  updated_at: string;
}

export interface OrchestratorDocumentOut {
  id: string;
  project_id: string;
  plan_id: string | null;
  filename: string;
  file_type: string;
  file_size: number;
  uploaded_by: string;
  created_at: string;
}

export interface ProjectListItem {
  id: string;
  name: string;
  description: string;
  domain: string;
  execution_mode: ExecutionMode;
  member_count: number;
  agent_count: number;
  run_count: number;
  inflight_count: number;
  completed_count: number;
  created_at: string;
  updated_at: string;
}

export interface AgentRegistryItem {
  id: string;
  name: string;
  short_name: string;
  description: string;
  category: string;
  status: string;
  total_runs: number;
  approved_runs: number;
  active_projects: number;
}

export interface AgentRunOut {
  id: string;
  project_id: string;
  agent_id: string;
  session_id: string | null;
  status: RunStatus;
  started_by: string;
  started_by_name: string;
  approved_by: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  rejection_reason: string;
  output_summary: string;
  created_at: string;
  updated_at: string;
}

export interface ConverterSourceField {
  name: string;
  start: number;
  length: number;
  data_type: "string" | "integer" | "decimal" | "boolean" | "date";
  scale?: number | null;
  source_format?: string | null;
  ddl_scale?: number | null;
}

export interface ConverterSourceFormat {
  path: string;
  format_type: "fixed_width" | "csv" | "tsv" | "json" | "jsonl" | "parquet";
  encoding: string;
  delimiter?: string;
  has_header: boolean;
  fields: ConverterSourceField[];
  row_limit?: number | null;
}

export interface ConverterMapping {
  source: string;
  target: string;
  target_type: string;
  transforms?: string[];
  nullable?: boolean;
  default?: string | number | boolean | null;
}

export interface ConverterAzureBlobStagingConfig {
  account_name: string;
  container: string;
  auth_mode: "sas_token" | "account_key" | "aad_service_principal";
  credential_value: string;
  tenant_id?: string | null;
  client_id?: string | null;
}

export interface ConverterTarget {
  table_name: string;
  schema_name?: string;
  if_exists: "fail" | "replace" | "append" | "truncate";
  chunk_size: number;
  reject_file_path?: string | null;
  // Snowflake/Synapse only — see agents/data_converter/patterns.py's
  // LoadTarget.bulk_load docstring for why the two dialects need opposite
  // handling under the hood; the UI only exposes the toggle + (for Synapse)
  // the Blob staging fields, not that distinction.
  bulk_load?: boolean;
  synapse_blob_staging?: ConverterAzureBlobStagingConfig;
  // Build & Preview's "exclude flagged records" checkbox — re-applies the
  // same incomplete-row heuristic at load time (service.py's
  // _flag_incomplete_rows) and drops those rows before the DB write. No
  // effect on Snowflake bulk loads (those skip row-level transform).
  exclude_incomplete_rows?: boolean;
}

export interface ConverterTask {
  source: ConverterSourceFormat;
  mappings: ConverterMapping[];
  target: ConverterTarget;
  copybook_text?: string | null;
}

export interface ConverterBadRecord {
  row_number: number;
  reason: string;
  row: Record<string, unknown>;
}

export interface ConverterPreviewResponse {
  rows_transformed: number;
  rows_rejected: number;
  preview: Record<string, unknown>[];
  rejects: Record<string, unknown>[];
  columns: string[];
  // Rows that parsed "successfully" (no type/required-field cast failure)
  // but are mostly empty compared to their peers — e.g. a truncated/corrupt
  // source line. See service.py's _flag_incomplete_rows.
  bad_records_count: number;
  bad_records: ConverterBadRecord[];
}

export interface ConverterLoadResponse {
  table_name: string;
  schema_name: string;
  rows_loaded: number;
  chunks_loaded: number;
  columns_loaded: string[];
  rows_rejected: number;
  reject_file: string | null;
  reject_reasons?: { reason: string; count: number }[];
  rows_excluded_incomplete?: number;
  table_created?: boolean;
  // Rows read from the source file before insert -- null for the two raw-
  // file bulk-load paths (Snowflake COPY INTO from disk/an external stage)
  // that never materialize a dataframe to count.
  rows_read?: number | null;
  // Non-fatal load-time notices, e.g. a large synapse/mssql load that ran
  // one round trip per row without bulk_load=True (PR #627 review item #B).
  warnings?: string[];
}

export interface ConverterDemoScenario {
  name: string;
  description: string;
  source_file: string;
  reject_file: string;
  copybook_text: string;
  task: ConverterTask;
}

// ── Data Converter — File Source (upload + layout + auto table + load) ────

export interface ConverterFileUploadResponse {
  file_id: string;
  file_path: string;
  filename: string;
  size_bytes: number;
  // Relative URL for GET /file/download/{file_id} -- lets the caller offer
  // a direct download of this exact registered file (e.g. a PDF/legacy
  // consolidation's generated CSV) so it can be loaded again later without
  // re-running the import flow.
  download_url: string;
  // Set only when the upload was an Excel workbook, auto-converted to CSV
  // server-side (Excel isn't itself a loadable File Source format). When
  // excel_sheet_count > 1, other sheets existed but weren't used -- the
  // caller should tell the user which one was picked.
  excel_sheet_used?: string | null;
  excel_sheet_count?: number;
}

export interface ConverterLayoutParseResponse {
  field_count: number;
  fields: ConverterSourceField[];
}

// SFTP File Source entry point -- request-scoped connection, never
// persisted (mirrors the Salesforce/Dataverse connect flow's shape).
export interface ConverterSftpConnectionConfig {
  host: string;
  port?: number;
  username?: string;
  password?: string | null;
  private_key?: string | null;
  private_key_passphrase?: string | null;
  remote_path?: string;
  // SHA256 host-key fingerprint (e.g. `ssh-keygen -lf <hostkey> -E sha256`
  // output). When set, the connection is rejected unless the server's host
  // key matches; when omitted, the server falls back to AutoAddPolicy
  // (PR #627 review item #A).
  host_key_fingerprint?: string | null;
}

export interface ConverterSftpFileMeta {
  name: string;
  path: string;
  size: number;
  modified_at: string | null;
  is_dir: boolean;
}

// SharePoint List File Source entry point -- request-scoped connection,
// never persisted (mirrors ConverterSftpConnectionConfig's convention).
export interface ConverterSharePointConnectionConfig {
  tenant_id: string;
  client_id: string;
  client_secret: string;
  site_url: string;
}

export interface ConverterSharePointListMeta {
  id: string;
  name: string;
  item_count: number;
}

// Extracted-table entries from PDF table extraction -- each extends the
// normal ConverterFileUploadResponse shape (already registered as a File
// Source via upload_source_file) with the source table's own metadata.
// `source_file_count` is only present on a consolidated multi-file entry
// (see converterPdfExtractTablesMulti) -- page_number is 0 for that entry
// since it spans several source files rather than one page.
export interface ConverterPdfExtractedTable extends ConverterFileUploadResponse {
  table_index: number;
  page_number: number;
  row_count: number;
  column_count: number;
  columns: string[];
  source_file_count?: number;
}

export interface ConverterPdfExtractTablesMultiResponse {
  tables: ConverterPdfExtractedTable[];
  errors: { filename: string; error: string }[];
}

// Read-only preview of what pdfplumber extracted from one or more
// already-uploaded PDFs -- columns plus a capped sample of rows per
// detected table (see converterPreviewPdfTables). Distinct from
// ConverterPdfExtractedTable, which describes an already-*registered*
// File Source; this carries actual cell values for a quick sanity check
// before committing to "Parse & Continue", and nothing here is persisted.
export interface ConverterPdfPreviewTable {
  table_index: number;
  page_number: number;
  columns: string[];
  rows: string[][];
  row_count: number;
  column_count: number;
  truncated: boolean;
}

export interface ConverterPdfPreviewFile {
  filename: string;
  tables: ConverterPdfPreviewTable[];
  error: string | null;
}

export interface ConverterPdfPreviewResponse {
  files: ConverterPdfPreviewFile[];
}

export interface ConverterBuildFileTaskResponse {
  task: ConverterTask;
}

export interface ConverterTestConnectionResponse {
  status: string;
  dialect: string;
}

// ── Data Converter — RDS + Salesforce STTM types ──────────────────────────

export interface DbConnectionConfig {
  db_type:
    | "oracle"
    | "postgres"
    | "mysql"
    | "mssql"
    | "synapse"
    | "snowflake"
    | "databricks"
    | "snowflake"
    | "jdbc_bridge" // mainframe DB2 for z/OS / ADABAS SQL Gateway / any JDBC source
    | "mainframe_copybook"; // flat-file + COBOL copybook, no live connection
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  schema_name: string;
  service_name?: string | null;
  http_path?: string | null; // Databricks SQL warehouse HTTP path
  client_id?: string | null; // Databricks OAuth machine-to-machine
  client_secret?: string | null; // Databricks OAuth machine-to-machine
  // snowflake — account identifier (e.g. "xy12345.us-east-1") + the virtual
  // warehouse/role to use once already provisioned. The backend model
  // (agents/data_converter/patterns.py's DbConnectionConfig) has carried
  // these since Snowflake support was added; this frontend type never did,
  // so a fresh Snowflake "New Connection" through ConnectDatabaseDialog
  // silently dropped them on the way to the API (every Snowflake connection
  // used elsewhere in this app went through a saved connection_id instead,
  // which never touches this mapping).
  account?: string | null;
  warehouse?: string | null;
  role?: string | null;
  jdbc_driver_class?: string | null; // e.g. com.ibm.db2.jcc.DB2Driver
  jdbc_url?: string | null;
  jdbc_driver_jar_path?: string | null;
  bridge_label?: string | null; // free-text, e.g. "DB2 for z/OS"
  copybook_text?: string | null;
}

// A completed Oracle golden-record push from a real MDM agent run (see
// mdm_routes.py's Phase 1.10 output-config/push-golden-records + GET
// /oracle-outputs) — lets Data Converter's MDM Lookup auto-discover a real
// run's output table instead of the user typing schema/table in by hand.
// Credentials are never included (same as GET .../output-config); the user
// still supplies a username/password to query it.
export interface MdmOracleOutput {
  run_id: string;
  project_id: string;
  run_name: string;
  host: string | null;
  port: number | null;
  service_name: string | null;
  schema_name: string | null;
  table_name: string | null;
  pushed_count: number;
  completed_at: string | null;
}

// A Data Converter connection call site can supply either a fresh set of
// credentials, or a reference to one of the project's saved connections
// (resolved server-side — see ztd_app's
// admin/project_db_config.resolve_connect_config) — never both. Every
// converter* function that takes a connection accepts this union instead
// of a raw DbConnectionConfig so the frontend never has to hold onto raw
// credentials once a connection is saved.
export type ConverterConnectionRef =
  | DbConnectionConfig
  | { connectionId: string; projectId: string | undefined };

function converterConnRefFields(
  prefix: "source" | "target" | "",
  ref: ConverterConnectionRef,
): Record<string, unknown> {
  const connKey = prefix ? `${prefix}_connection` : "connection";
  if ("connectionId" in ref) {
    return {
      [`${connKey}_id`]: ref.connectionId,
      project_id: ref.projectId,
    };
  }
  return { [connKey]: ref };
}

export interface ColumnMeta {
  name: string;
  data_type: string;
  length?: number | null;
  precision?: number | null;
  scale?: number | null;
  nullable: boolean;
  is_primary_key: boolean;
  is_foreign_key: boolean;
  fk_references?: string | null;
}

export interface TableMeta {
  table_name: string;
  schema_name: string;
  columns: ColumnMeta[];
  row_count_estimate?: number | null;
  // Populated only when this table's metadata came from an uploaded file
  // ("Generate from Legacy Code") -- the original filename it was
  // extracted from. "" for a live database scan.
  source_file?: string;
  // Client-side only — never returned by a single-connection scan response.
  // Set by DataConverterView when merging an "Add Another Source
  // Connection" scan into schemaResult.tables, so a table's originating
  // connection survives into TableObjectMapping.source_connection_id.
  // undefined means "the project's primary/default source connection",
  // identical to every scan before this field existed.
  source_connection_id?: string;
}

export interface SchemaDiff {
  tables_added: string[];
  tables_removed: string[];
  tables_changed: {
    table_name: string;
    columns_added: string[];
    columns_removed: string[];
    columns_modified: { column: string; changes: string[] }[];
  }[];
  unchanged_table_count: number;
  has_changes: boolean;
}

export interface SchemaExtractResult {
  db_type: string;
  schema_name: string;
  tables: TableMeta[];
  extracted_at: string;
  // Only populated by /target/scan when a project_id is supplied — the
  // diff against that project's last target-schema scan (null on the
  // first-ever scan, since there's nothing to compare against).
  schema_changes?: SchemaDiff | null;
  // Reported rather than aborting: a per-file parse failure or cross-file
  // table-name collision (the bulk /source/parse-legacy-batch entry point,
  // which always returns the list, possibly empty), and any uploaded file
  // that was parsed but could not be stored for reuse (name too long, ...).
  // The single-file /source/parse-legacy and /target/parse-legacy set it
  // only when there is something to report, so treat absent as [].
  warnings?: string[];
  // Populated by /source/parse-legacy(-batch) for every uploaded file that
  // was ALSO persisted to object storage as a re-loadable data file (CSV/
  // TSV/JSON/JSONL/Parquet — the formats the Load step's "Files" mode
  // reads), keyed by original filename. Lets the Load step auto-find each
  // mapped table's source file (via TableMeta.source_file) instead of
  // asking the user to re-upload every table's file one at a time.
  file_refs?: Record<string, ConverterFileUploadResponse>;
  // Only populated by SSIS (.dtsx) parses, keyed by originating filename —
  // a structural breakdown of everything else the package contained, for
  // manual review. Never includes connection strings or variable values.
  package_details?: Record<string, SsisPackageDetails>;
  // Populated for SQL DDL, COBOL copybook, and XML parses, keyed by
  // originating filename — everything found in the file that ISN'T
  // reflected as a table/column (views/indexes/procedures/triggers/
  // sequences for SQL; REDEFINES/OCCURS/88-level condition names for
  // copybooks; sibling elements for XML), for manual review. Empty for
  // every other format (SSIS uses package_details instead).
  additional_items?: Record<string, AdditionalItem[]>;
  // Only populated by /source/parse-legacy-pdf -- non-null when several
  // similar-layout PDFs were merged into one source table (see
  // converterParseLegacyPdf's `consolidate` option), null otherwise.
  consolidation_note?: string | null;
}

export interface AdditionalItem {
  category: string;
  name: string;
  detail: string;
}

export interface SsisPackageDetails {
  connection_managers: { name: string; creation_name: string }[];
  executables: { name: string; type: string }[];
  data_flow_components: {
    name: string;
    component_class_id: string;
    role: string;
    column_count: number;
  }[];
  variables: { name: string; data_type: string }[];
}

export interface SalesforceConnectionConfig {
  // Dynamics 365 and Power Platform share one connector (both run on the
  // same underlying Dataverse Web API + Azure AD OAuth2 login) — target_type
  // is purely a label, not a different integration. Workday reuses the same
  // tenant_id/client_id/client_secret/resource_url fields (see the backend's
  // SalesforceConnectionConfig docstring for the exact field-reuse mapping)
  // plus one new field, refresh_token, below.
  target_type: "salesforce" | "dynamics365" | "power_platform" | "workday";
  // Salesforce
  instance_url: string;
  username: string;
  password: string;
  security_token: string;
  consumer_key?: string | null;
  consumer_secret?: string | null;
  // Dynamics 365 / Power Platform (Dataverse) — Azure AD OAuth2 client-credentials.
  // Also reused for Workday (see target_type's comment above).
  tenant_id?: string | null;
  client_id?: string | null;
  client_secret?: string | null;
  resource_url?: string | null;
  // Workday Prism Analytics' OAuth2 refresh_token grant credential.
  refresh_token?: string | null;
}

export interface SalesforceField {
  api_name: string;
  label: string;
  data_type: string;
  length?: number | null;
  required: boolean;
  nillable: boolean;
  unique: boolean;
  custom: boolean;
  external_id?: boolean;
  id_lookup?: boolean;
  picklist_values?: string[];
  reference_to?: string[];
}

export interface SalesforceObjectMeta {
  api_name: string;
  label: string;
  object_type: "standard" | "custom";
  fields: SalesforceField[];
}

export interface SttmFieldMapping {
  source_column: string;
  // Which physical source table source_column comes from, for a complex
  // (multi-table-join) mapping row. Absent/null for a simple 1:1 row.
  source_table_ref?: string | null;
  source_type: string;
  target_field: string;
  target_type: string;
  target_length?: number | null;
  // The matched target field's REAL declared type (e.g. "picklist",
  // "reference") before target_type above was forced to "string" for
  // staging — "" when unknown/not applicable.
  target_field_type?: string;
  // Populated only when target_field_type is "picklist"/"multipicklist" —
  // the field's valid values, for an accurate value-lookup transform.
  picklist_values?: string[];
  // Populated only when target_field_type is "reference" — the target
  // object(s) this lookup/master-detail field points at.
  reference_to?: string[];
  transformation: string;
  validation_rule: string;
  notes: string;
  confidence: number;
  status: "Mapped" | "Unmapped" | "Review";
  // Set only when the owning TableObjectMapping has group_by_columns —
  // wraps this field in the given SQL aggregate when building the load
  // SELECT (e.g. a Gold fact-table rollup: SUM(amount), COUNT(*)). A field
  // mapping with neither this nor a place in group_by_columns is dropped
  // from an aggregate mapping's SELECT (agents/data_converter/patterns.py's
  // SttmFieldMapping.aggregate_function).
  aggregate_function?: "sum" | "count" | "avg" | "min" | "max" | null;
}

export interface JoinCondition {
  left_table: string;
  right_table: string;
  left_column: string;
  right_column: string;
  join_type: "inner" | "left" | "right" | "full" | "cross";
}

export interface TableObjectMapping {
  source_table: string;
  // Full source-table list for a complex (multi-table-join) mapping row;
  // [] or absent means an ordinary simple 1:1 row.
  source_tables?: string[];
  joins?: JoinCondition[];
  join_sql_preview?: string;
  // Optional user-authored SQL WHERE-clause fragment (no leading "WHERE"),
  // applied to this table's SELECT at Load to Target to filter which source
  // rows are extracted. "" or absent means no filter.
  where_clause?: string;
  // 2026-09-02, requested by user: optional user-edited full SELECT that
  // REPLACES the auto-generated one for this table's load (rather than
  // just filtering it, like where_clause). "" or absent means "keep
  // auto-generating as today". Re-validated server-side before use.
  custom_load_sql?: string;
  source_schema: string;
  target_object: string;
  target_label: string;
  confidence: number;
  field_mappings: SttmFieldMapping[];
  // Carried over from the matched source table's TableMeta.source_file --
  // "" unless this table's schema came from an uploaded legacy-code/
  // metadata file. Lets the Load step's "Files" mode auto-find the same
  // file instead of asking the user to re-select it for every table.
  source_file?: string;
  // Non-empty means this mapping loads a GROUP BY rollup (a Gold-layer
  // fact table, typically) instead of a flat per-row copy — target column
  // names to group by; every OTHER field_mapping must carry its own
  // aggregate_function or it's dropped from the SELECT
  // (agents/data_converter/patterns.py's TableObjectMapping.group_by_columns).
  group_by_columns?: string[];
  // Which of the project's saved SOURCE connections (see
  // admin/project_db_config.list_connections_for_project) this row's
  // source table(s) should be read from at Load to Target — undefined
  // (default) means "use the job's default source connection", identical
  // to every mapping before this field existed. Lets a project mix source
  // tables from several different source databases/schemas across mapping
  // rows in one Load Data run.
  source_connection_id?: string;
  // Heterogeneous (cross-connection) join support: a per-table override of
  // which saved source connection/schema a JOINED-IN table (any table
  // other than this mapping's own hub/source_connection_id) should be read
  // from. Keyed by table name; a table absent from these dicts (the common
  // case, including every single-connection mapping) uses this mapping's
  // own source_connection_id/source_schema, identical to today. See
  // agents/data_converter/patterns.py's resolve_table_connection_id /
  // resolve_table_schema / is_heterogeneous_join.
  join_table_connection_ids?: Record<string, string | null>;
  join_table_schemas?: Record<string, string>;
  // Issue #599 — incremental (CDC-lite) Data Loader. "incremental" filters
  // this table's SELECT to rows where `incremental_column` is greater than
  // the last persisted watermark, then advances the watermark to the
  // highest value actually read. Append-only: never detects updates to
  // already-loaded rows or deletes. Only supported for a plain (non-join,
  // non-MDM-lookup, non-custom-SQL, non-aggregate) mapping. Undefined/
  // "full" (default) means every row is read every run, same as today.
  load_mode?: "full" | "incremental";
  // Required when load_mode is "incremental" — must be one of this
  // mapping's own field_mappings[].source_column values (manual selection
  // only; no auto-detection). Should be a monotonically increasing column
  // (a timestamp or auto-incrementing id).
  incremental_column?: string;
}

export interface ComplexMappingGroup {
  sourceTables: string[];
  joins: JoinCondition[];
  targetObject: string;
  // Table name -> the subset of its columns to include in the merged
  // mapping. A table absent from this (or the field omitted entirely)
  // still gets every one of its columns.
  includedColumns?: Record<string, string[]>;
  // See TableObjectMapping.join_table_connection_ids/join_table_schemas --
  // same shape, passed straight through to /mapping/generate-complex so a
  // join spanning tables from more than one saved source connection can be
  // built (the hub table's own connection is taken from wherever this
  // mapping already gets it, e.g. the picked table's own source scan tag).
  tableConnectionIds?: Record<string, string | null>;
  tableSchemas?: Record<string, string>;
}

export interface SttmGenerateResponse {
  file_id: string;
  filename: string;
  download_url: string;
  total_mapping_rows: number;
  table_count: number;
}

// 2026-09-02, requested by user: the .xlsx mapping workbook auto-saved at
// Finalize — all fields null until a project's mapping has a generated
// workbook (see GET/POST /project/{id}/mapping/xlsx).
export interface ProjectMappingFileOut {
  file_id: string | null;
  filename: string | null;
  download_url: string | null;
}

export interface DataDictionaryGenerateResponse {
  file_id: string;
  filename: string;
  download_url: string;
  total_rows: number;
  table_count: number;
}

// One of the four deliverables produced by a SAP BODS ETL dataflow import
// (see converterParseBodsDataflowBatch) -- STTM mapping, source DDL, target
// DDL, or the Job/Workflow/Dataflow audit workbook. Downloaded via GET
// /bods/download/{file_id}, a separate route from /sttm/download/{file_id}
// since two of the four are .sql rather than .xlsx.
export interface ConverterBodsDataflowDoc {
  file_id: string;
  filename: string;
  download_url: string;
}

export interface ConverterBodsDataflowImportResponse {
  sttm: ConverterBodsDataflowDoc;
  source_ddl: ConverterBodsDataflowDoc;
  target_ddl: ConverterBodsDataflowDoc;
  audit: ConverterBodsDataflowDoc;
  table_count: number;
  mapping_count: number;
  total_mapping_rows: number;
  source_table_count: number;
  target_table_count: number;
  warnings: string[];
}

// One of the four deliverables produced by an Informatica PowerCenter
// mapping import (see converterParseInformaticaMappingBatch) -- STTM
// mapping, source DDL, target DDL, or the Folder/Mapping/Target audit
// workbook. Downloaded via GET /informatica/download/{file_id}, its own
// route for the same reason ConverterBodsDataflowDoc's is.
export interface ConverterInformaticaMappingDoc {
  file_id: string;
  filename: string;
  download_url: string;
}

export interface ConverterInformaticaMappingImportResponse {
  sttm: ConverterInformaticaMappingDoc;
  source_ddl: ConverterInformaticaMappingDoc;
  target_ddl: ConverterInformaticaMappingDoc;
  audit: ConverterInformaticaMappingDoc;
  table_count: number;
  mapping_count: number;
  total_mapping_rows: number;
  source_table_count: number;
  target_table_count: number;
  warnings: string[];
}

export interface RulesApplyResponse extends SttmGenerateResponse {
  rules_applied: number;
  mappings: TableObjectMapping[];
}

export interface CustomRule {
  target_field: string;
  validation_rule: string;
  transformation?: string;
  status?: string;
}

export interface TransformRulesImportResponse {
  mappings: TableObjectMapping[];
  rules_applied: number;
  applied: {
    table: string;
    column: string;
    dimension: string;
    transformation: string;
  }[];
  unmatched: { table: string; column: string; dimension: string }[];
}

// A reviewer's per-rule choice after "Import Transformation Rules": either
// exclude rows violating `logic` (a boolean condition, e.g. "BIRTH_DATE >
// today()") from the load and report them, or include the row with `logic`
// applied as the field's real Transform text — for "include", `description`
// (the rule's original, unedited plain-English description) is what scopes
// that transform to only the rows actually violating the rule, rather than
// every row.
export interface TransformRuleDecision {
  table: string;
  column: string;
  action: "exclude" | "include";
  logic: string;
  description?: string;
}

export interface ApplyRuleDecisionsResponse {
  mappings: TableObjectMapping[];
  decisions_applied: number;
  not_found: { table: string; column: string }[];
}

// ── Legacy transformation-rule import (Excel/SQL/PL-SQL/SSIS/Informatica/BODS) ──

export type LegacyArtifactType =
  | "excel"
  | "sql"
  | "plsql"
  | "ssis"
  | "informatica"
  | "bods"
  | "json"
  | "xml";

export type LegacyMatchStatus = "matched" | "unmatched";
export type LegacyReviewStatus = "pending" | "approved" | "rejected" | "edited";

export interface LegacyRuleItem {
  id: string;
  batch_id: string;
  source_location: string;
  source_table: string | null;
  source_column: string | null;
  target_table: string | null;
  target_column: string | null;
  expression: string;
  raw_snippet: string;
  confidence: number;
  notes: string;
  match_status: LegacyMatchStatus;
  matched_target_table: string | null;
  matched_target_field: string | null;
  match_reason: string;
  review_status: LegacyReviewStatus;
  final_transformation: string | null;
  review_notes: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface LegacyRuleBatch {
  id: string;
  project_id: string | null;
  source_filename: string;
  artifact_type: LegacyArtifactType;
  imported_by: string | null;
  item_count: number;
  matched_count: number;
  unmatched_count: number;
  created_at: string;
  items?: LegacyRuleItem[];
}

export interface DdlStatement {
  table_name: string;
  ddl: string;
}

export interface DdlDuplicate {
  type: "merged_table" | "duplicate_table" | "duplicate_column";
  table: string;
  column?: string;
  detail: string;
}

// A user decision from the DDL duplicate-review UI, sent back on
// "Apply & Regenerate DDL" to override the default silent-remove behavior.
// `column` is omitted/undefined for a table-level duplicate.
export interface DdlDuplicateResolution {
  table: string;
  column?: string;
  action: "remove" | "keep" | "rename";
  new_name?: string;
}

export interface DdlGenerateResponse {
  statement_count: number;
  ddl_statements: DdlStatement[];
  duplicates_removed: number;
  duplicate_report: DdlDuplicate[];
}

export interface DdlExecuteResult {
  ddl: string;
  status: "success" | "error";
  error: string | null;
}

export interface DdlExecuteResponse {
  executed: number;
  success: number;
  failed: number;
  results: DdlExecuteResult[];
}

export interface DdlReconcileResult {
  table_name: string;
  action: "create" | "recreate" | "up_to_date";
  diff: string[];
}

export interface DdlReconcileResponse {
  results: DdlReconcileResult[];
}

export interface DdlDropResult {
  table_name: string;
  status: "success" | "skipped" | "error";
  error: string | null;
}

export interface DdlDropResponse {
  dropped: number;
  skipped: number;
  failed: number;
  results: DdlDropResult[];
}

export interface LoaderSqlPreviewOut {
  select_sql: string;
  insert_sql: string;
}

export interface DataLoadTableResult {
  source_table: string;
  target_table: string;
  source_total_count: number;
  rows_read: number;
  rows_loaded: number;
  rows_failed: number;
  rows_dq_excluded?: number;
  error: string | null;
  // Download URL + top distinct error messages for this table's rejected
  // rows (file-mode jobs only -- see _run_file_bulk_load_job /
  // load_conversion's "reject_file"/"reject_reasons"). Absent/null when
  // there were no rejects, or for job types that don't track per-row
  // rejects this way.
  reject_file?: string | null;
  reject_reasons?: { reason: string; count: number }[] | null;
  // Wall-clock time this table's own load took (file-mode jobs only --
  // see _run_file_bulk_load_job). Absent/null for job types that don't
  // time individual tables.
  duration_ms?: number | null;
  // Client-only flag (never sent by the backend) -- set by
  // mergeCarryForwardIntoLoadStatus to mark a result carried over from a
  // prior run rather than actually reprocessed by the current job, so the
  // UI can show "kept from earlier run" instead of implying a fresh reload.
  carried_forward?: boolean;
}

export interface DataLoadTableProgress {
  rows_loaded: number;
  rows_failed: number;
}

export interface DqReportInfo {
  filename: string;
  content_base64: string;
  modified_count: number;
}

export interface DataLoadStartResponse {
  job_id: string;
}

export interface DataLoadStatusResponse {
  job_id: string;
  status: "running" | "completed" | "cancelled" | "failed";
  total_tables: number;
  tables_done: number;
  current_table: string | null;
  // Same tables as current_table, as a real list rather than a ", "-joined
  // display string -- prefer this when present (current_table is kept for
  // backward compatibility with older job rows/callers).
  current_tables?: string[] | null;
  total_rows_loaded: number;
  total_rows_failed: number;
  table_results: DataLoadTableResult[];
  table_progress: Record<string, DataLoadTableProgress>;
  dq_report: DqReportInfo | null;
  errors: string[];
  cancel_requested: boolean;
  dry_run?: boolean;
}

export interface DbToSfLoadResponse {
  sf_object: string;
  rows_read: number;
  rows_loaded: number;
  rows_failed: number;
  errors: string[];
}

export interface DbToSfLoadStartResponse {
  job_id: string;
}

export interface DbToSfLoadStatusResponse {
  job_id: string;
  status: "running" | "completed" | "cancelled" | "failed";
  sf_object: string;
  rows_read: number;
  rows_loaded: number;
  rows_failed: number;
  batches_done: number;
  batches_total: number;
  errors: string[];
  cancel_requested: boolean;
}

// CRM/SaaS -> DB streaming load (issue #735) -- never materializes the
// full object in memory/CSV, unlike converterParseLegacyCrm's flow.
export interface CrmToDbFieldMapping {
  source_field: string;
  target_column: string;
  value_map?: Record<string, string> | string;
}

export interface CrmToDbLoadStartResponse {
  job_id: string;
}

export interface CrmToDbLoadStatusResponse {
  job_id: string;
  status: "running" | "completed" | "cancelled" | "failed";
  object_name: string;
  rows_read: number;
  rows_loaded: number;
  rows_failed: number;
  batches_done: number;
  errors: string[];
  cancel_requested: boolean;
}

// ── Reconciliation Dashboard ────────────────────────────────────────────────

export interface TableCountResult {
  source_table: string;
  target_table: string;
  source_count: number | null;
  target_count: number | null;
  match: boolean;
  difference: number | null;
  mdm_lookup_count: number | null;
  mdm_matched: boolean;
  source_is_joined: boolean;
  error: string | null;
}

export interface ReconcileCountResponse {
  results: TableCountResult[];
}

export interface ReconcileRowLimitsResponse {
  row_reconcile_max_rows: number;
}

export interface ColumnReconcileResult {
  source_column: string;
  target_field: string;
  source_type: string;
  target_type: string;
  // null when a mapped source_column isn't found among the uploaded
  // file's actual columns (stale schema, re-uploaded file, case mismatch).
  source_null_count: number | null;
  target_null_count: number;
  source_distinct_count: number | null;
  target_distinct_count: number | null;
  status: "match" | "review";
}

export interface ReconcileColumnResponse {
  source_table: string;
  target_table: string;
  source_total_rows: number;
  target_total_rows: number;
  source_is_joined: boolean;
  columns: ColumnReconcileResult[];
}

export interface RowMismatchColumnDiff {
  source_column: string;
  target_field: string;
  source_value: string | null;
  target_value: string | null;
}

export interface RowMismatchDetail {
  key: Record<string, string | null>;
  differing_columns: RowMismatchColumnDiff[];
}

export interface RowSummaryDetail {
  key: Record<string, string | null>;
  values: Record<string, string | null>;
}

export interface FieldReconcileStat {
  source_column: string;
  target_field: string;
  matched_count: number;
  mismatched_count: number;
}

export interface ReconcileRowResponse {
  source_table: string;
  target_table: string;
  key_columns: string[];
  source_total_rows: number;
  target_total_rows: number;
  matched_rows: number;
  mismatched_rows: number;
  missing_in_target: number;
  missing_in_source: number;
  // True when either side's scan hit the internal row-count safety cap
  // before finishing — the counts above describe only the scanned
  // portion, not necessarily the whole table.
  truncated: boolean;
  max_rows_scanned: number;
  mismatches: RowMismatchDetail[];
  mismatches_truncated: boolean;
  missing_in_target_rows: RowSummaryDetail[];
  missing_in_target_truncated: boolean;
  missing_in_source_rows: RowSummaryDetail[];
  missing_in_source_truncated: boolean;
  field_stats: FieldReconcileStat[];
}

// File-source counterpart to ReconcileCountRequest/ReconcileColumnRequest —
// a Files-mode load has no source DB connection to reconcile against, so
// the source side reads each mapped table's own uploaded file instead.
export interface FileReconcileSourceFile {
  path: string;
  format_type: ConverterSourceFormat["format_type"];
  encoding: string;
  has_header: boolean;
  fields: ConverterSourceField[];
}

// ── Agent session summary ──────────────────────────────────────────────────

export interface AgentSessionSummary {
  id: string;
  /** Use this (not `id`) to resume a session -- see backend comment on why. */
  resume_key: string;
  session_type: string;
  label: string;
  status: string;
  file_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectActivityOut {
  runs: AgentRunOut[];
  sessions: AgentSessionSummary[];
  orchestrator_plans: OrchestratorPlanOut[];
}

// ---------------------------------------------------------------------------
// On-demand project purge (#680 phases 3/4) — mirrors
// ondemand_purge_service.py's receipt shape for both the real purge and the
// read-only pre-flight preview.
// ---------------------------------------------------------------------------
export type PurgeStoreStatus =
  | "purged"
  | "would_purge"
  | "skipped_no_policy"
  | "skipped_inactive_policy"
  | "skipped_legal_hold"
  | "skipped_unsupported_disposal_method"
  | "skipped_unmapped_category"
  | "no_preview_available"
  // #903 — one store's outcome no longer aborts the run:
  | "nothing_to_purge" // ran, found nothing left (no DisposalRecord written)
  | "failed" // raised; its data (if any) is untouched
  | "incomplete" // removed some, kept the rest (counts say how many)
  | "not_attempted" // never started: the run was interrupted first
  | "preview_failed" // preview only: that store could not be counted
  | "would_not_attempt"; // preview only: ops history would not run (legal hold on a dependent store)

export interface PurgeStoreEntry {
  store: string;
  describes?: string;
  status: PurgeStoreStatus;
  policy_id?: string;
  disposal_method?: string;
  counts?: Record<string, number>;
  /** failed / incomplete: the exception class, never client data. */
  error_type?: string;
  /** failed / incomplete: a safe reason (error code), when one exists. */
  error?: string;
  /** incomplete: why the run stopped ("soft time limit" | "cancelled"). */
  interrupted?: string;
  /** not_attempted: why it was deliberately not started (e.g. a dependency did not finish). */
  reason?: string;
  /** ops history only: what full_erasure deliberately spared (profiling runs that still own
   * DQ findings no retention rule authorised deleting). */
  retained?: Record<string, number>;
  failed_item_ids?: string[];
}

export interface PurgePreviewOut {
  project_id: string;
  tenant_id: string;
  full_erasure_would_apply: boolean;
  stores: PurgeStoreEntry[];
}

export interface PurgeReceiptOut {
  project_id: string;
  tenant_id: string;
  requested_by: string;
  started_at: string;
  completed_at: string;
  full_erasure_applied: boolean;
  /** #903: true when any store failed / was incomplete / was not attempted.
   * Such a job has status "failed" (never "completed") and no certificate. */
  partial: boolean;
  stores: PurgeStoreEntry[];
}

export interface PurgeJobOut {
  job_id: string;
  project_id: string;
  status: "queued" | "running" | "completed" | "failed";
  requested_by: string;
  result: PurgeReceiptOut | null;
  error_detail: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Auth — login returns the user profile; JWT lives in an HttpOnly cookie
// ---------------------------------------------------------------------------
/** @deprecated Use UserOut directly — login no longer returns access_token in body */
export interface LoginResponse {
  user: AuthUser;
}

// ---------------------------------------------------------------------------
// Data Validation — mirrors ztd_app/api/modules/data_validation/schemas.py.
// The router returns DryRunResponse directly (no APIEnvelope wrapper).
// ---------------------------------------------------------------------------
export type ValidationStrictness = "loose" | "standard" | "strict";

export interface ValidationRulePackPreview {
  rule_id: string;
  category: string;
  severity: string;
  description: string;
  applies_to: string;
}

export interface ValidationDryRunRequest {
  rule_pack?: string;
  rule_pack_version?: string;
  strictness?: ValidationStrictness;
  rule_pack_overlay?: string | null;
  rule_pack_overlay_version?: string | null;
}

export interface ValidationDryRunResponse {
  rule_pack: string;
  rule_pack_version: string;
  strictness: ValidationStrictness;
  applied_severities: string[];
  rules: ValidationRulePackPreview[];
  rule_count: number;
}

// ---------------------------------------------------------------------------
// API methods
// ---------------------------------------------------------------------------
export const apiv2 = {
  // ── Auth ─────────────────────────────────────────
  // The backend exposes /api/v2/auth/* (see auth/v2_router.py).
  // Handlers return raw UserOut directly; no envelope adapter needed.
  /** POST /api/v2/auth/login — sets HttpOnly cookies, returns UserOut */
  login: (email: string, password: string) =>
    request<UserOut>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  /** POST /api/v2/auth/logout — clears auth cookies */
  logout: () =>
    request("/auth/logout", { method: "POST" }).catch(() => undefined),

  /** POST /api/v2/auth/refresh — extend the cookie TTL with a fresh token */
  refreshToken: () => request<UserOut>("/auth/refresh", { method: "POST" }),

  /** GET /api/v2/auth/me — current authenticated user */
  me: () => request<UserOut>("/auth/me"),

  // ── Users ────────────────────────────────────────
  listUsers: (activeOnly = false) =>
    request<UserOut[]>(`/users?active_only=${activeOnly}`),

  createUser: (data: {
    email: string;
    full_name: string;
    password: string;
    role_id: string;
  }) =>
    request<UserOut>("/users", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getUser: (id: string) => request<UserOut>(`/users/${id}`),

  /** Self-service profile/password update. Own account always allowed;
   * updating someone else's requires admin:tenant. */
  updateUser: (id: string, data: { full_name?: string; password?: string }) =>
    request<UserOut>(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  /** Admin-only: activate/deactivate a user (requires admin:tenant). */
  setUserActive: (id: string, isActive: boolean) =>
    request<UserOut>(`/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ is_active: isActive }),
    }),

  // ── Projects ─────────────────────────────────────
  listProjects: () => request<ProjectListItem[]>("/projects"),

  getProject: (id: string) => request<ProjectOut>(`/projects/${id}`),

  createProject: (data: {
    name: string;
    description?: string;
    requirements?: string;
    source_data_info?: string;
    domain?: string;
    tech_stack?: string;
    execution_mode?: ExecutionMode;
    goal?: string;
    agent_ids?: string[];
  }) =>
    request<ProjectOut>("/projects", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateProject: (
    id: string,
    data: {
      name?: string;
      description?: string;
      requirements?: string;
      source_data_info?: string;
      domain?: string;
      tech_stack?: string;
      execution_mode?: ExecutionMode;
      goal?: string;
    },
  ) =>
    request<ProjectOut>(`/projects/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteProject: (id: string) =>
    request<void>(`/projects/${id}`, { method: "DELETE" }),

  // ── On-demand purge (#680 phases 3/4) ────────────
  // Preview is read-only and synchronous. The real purge is async: POST
  // returns a job id immediately, poll getPurgeJob for the receipt.
  previewProjectPurge: (projectId: string) =>
    request<PurgePreviewOut>(`/projects/${projectId}/purge/preview`),

  purgeProjectData: (projectId: string) =>
    request<{ job_id: string; status: string }>(
      `/projects/${projectId}/purge`,
      {
        method: "POST",
      },
    ),

  getPurgeJob: (projectId: string, jobId: string) =>
    request<PurgeJobOut>(`/projects/${projectId}/purge/${jobId}`),

  // #680 phase 5 — the downloadable erasure attestation for a completed
  // purge job. Mirrors downloadOutput's blob-download pattern (below):
  // the PDF bytes never round-trip through request()'s JSON handling.
  downloadPurgeCertificate: async (projectId: string, jobId: string) => {
    const res = await fetch(
      `${BASE}/projects/${projectId}/purge/${jobId}/certificate`,
      { credentials: "include" },
    );
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        formatApiErrorDetail(
          body.detail,
          `Certificate download failed: ${res.status}`,
        ),
      );
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `purge-certificate-${projectId}-${jobId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // ── Project Activity ─────────────────────────────
  getProjectActivity: (projectId: string) =>
    request<ProjectActivityOut>(`/projects/${projectId}/activity`),

  // ── Delete individual work items ─────────────────
  deleteRun: (projectId: string, runId: string) =>
    request<void>(`/projects/${projectId}/runs/${runId}`, { method: "DELETE" }),

  deleteSession: (projectId: string, sessionId: string) =>
    request<void>(`/projects/${projectId}/sessions/${sessionId}`, {
      method: "DELETE",
    }),

  deletePlan: (projectId: string, planId: string) =>
    request<void>(`/projects/${projectId}/plans/${planId}`, {
      method: "DELETE",
    }),

  // ── Members ──────────────────────────────────────
  addMember: (
    projectId: string,
    userId: string,
    role: ProjectMemberRole = "member",
  ) =>
    request<ProjectMemberOut>(`/projects/${projectId}/members`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId, role }),
    }),

  removeMember: (projectId: string, userId: string) =>
    request<void>(`/projects/${projectId}/members/${userId}`, {
      method: "DELETE",
    }),

  // ── LLM Config ───────────────────────────────────
  getLLMConfig: (projectId: string) =>
    request<LLMConfigOut | null>(`/projects/${projectId}/llm-config`),

  setLLMConfig: (
    projectId: string,
    data: {
      provider?: string;
      model_name?: string;
      api_key?: string;
      temperature?: number;
      max_tokens?: number;
      additional_config?: Record<string, unknown>;
    },
  ) =>
    request<LLMConfigOut>(`/projects/${projectId}/llm-config`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteLLMConfig: (projectId: string) =>
    request<void>(`/projects/${projectId}/llm-config`, { method: "DELETE" }),

  // ── Agent selection ──────────────────────────────
  enableAgents: (projectId: string, agentIds: string[]) =>
    request<ProjectAgentOut[]>(`/projects/${projectId}/agents`, {
      method: "POST",
      body: JSON.stringify({ agent_ids: agentIds }),
    }),

  disableAgent: (projectId: string, agentId: string) =>
    request<void>(`/projects/${projectId}/agents/${agentId}`, {
      method: "DELETE",
    }),

  updateAgentConfig: (
    projectId: string,
    agentId: string,
    config: Record<string, unknown>,
  ) =>
    request<ProjectAgentOut>(`/projects/${projectId}/agents/${agentId}`, {
      method: "PUT",
      body: JSON.stringify({ config, enabled: true }),
    }),

  // ── Agent registry ───────────────────────────────
  listAgents: (includeComingSoon = true) =>
    request<AgentRegistryItem[]>(
      `/agents?include_coming_soon=${includeComingSoon}`,
    ),

  getAgent: (id: string) => request<AgentRegistryItem>(`/agents/${id}`),

  // ── Runs ─────────────────────────────────────────
  listRuns: (projectId: string) =>
    request<AgentRunOut[]>(`/projects/${projectId}/runs`),

  createRun: (projectId: string, agentId: string, sessionId?: string) =>
    request<AgentRunOut>(`/projects/${projectId}/runs`, {
      method: "POST",
      body: JSON.stringify({ agent_id: agentId, session_id: sessionId }),
    }),

  completeRun: (projectId: string, runId: string) =>
    request<AgentRunOut>(`/projects/${projectId}/runs/${runId}/complete`, {
      method: "POST",
    }),

  approveRun: (projectId: string, runId: string) =>
    request<AgentRunOut>(`/projects/${projectId}/runs/${runId}/approve`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  rejectRun: (projectId: string, runId: string, reason: string) =>
    request<AgentRunOut>(`/projects/${projectId}/runs/${runId}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  // ── LLM Providers & Global Config ────────────────
  listProviders: () => request<LLMProviderInfo[]>("/llm/providers"),

  getGlobalLLMConfig: () =>
    request<GlobalLLMConfigOut | null>("/llm/global-config"),

  setGlobalLLMConfig: (data: {
    provider: string;
    model_name: string;
    api_key?: string;
    temperature?: number;
    max_tokens?: number;
    additional_config?: Record<string, unknown>;
  }) =>
    request<GlobalLLMConfigOut>("/llm/global-config", {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  getLLMStatus: () =>
    request<{
      configured: boolean;
      online: boolean;
      provider: string | null;
      model: string | null;
      error: string | null;
    }>("/llm/status"),

  // ── Epoch gateway model lookup ───────────────────
  // Admin posts gateway URL + API key; backend validates by hitting
  // the catalog endpoint once and returns the filtered model list.
  // ``source`` is "live" when the gateway responded, "fallback" when
  // the request failed and the seeded DEFAULT_MODELS list was used.
  lookupEpochModels: (data: {
    gateway_url: string;
    api_key: string;
    refresh?: boolean;
  }) =>
    request<{
      source: "live" | "fallback";
      models: { id: string; label: string }[];
      error?: string;
    }>("/llm/epoch/models", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Lightweight reachability probe — hits GET {gateway_url}/v1/models.
  // No inference cost. Empty body uses the saved global config so the
  // admin can probe the live production config with one click.
  probeEpoch: (data?: { gateway_url?: string; api_key?: string }) =>
    request<{
      ok: boolean;
      gateway_url: string;
      status_code: number | null;
      latency_ms: number;
      model_count: number;
      error: string | null;
    }>("/llm/epoch/probe", {
      method: "POST",
      body: JSON.stringify(data || {}),
    }),

  // ── Epoch spend (admin) ──────────────────────────
  listEpochSpend: (period?: string) =>
    request<
      {
        project_id: string;
        period: string;
        total_cost_usd: number;
        call_count: number;
        base_cap_usd: number;
        override_amount_usd: number;
        effective_cap_usd: number;
        remaining_usd: number;
        blocked: boolean;
      }[]
    >(`/llm/epoch/spend${period ? `?period=${period}` : ""}`),

  getProjectEpochSpend: (projectId: string, period?: string) =>
    request<{
      project_id: string;
      period: string;
      total_cost_usd: number;
      call_count: number;
      base_cap_usd: number;
      override_amount_usd: number;
      effective_cap_usd: number;
      remaining_usd: number;
      blocked: boolean;
    }>(`/llm/epoch/spend/${projectId}${period ? `?period=${period}` : ""}`),

  setEpochSpendOverride: (
    projectId: string,
    data: { amount_usd: number; reason: string },
  ) =>
    request<{
      project_id: string;
      period: string;
      total_cost_usd: number;
      call_count: number;
      base_cap_usd: number;
      override_amount_usd: number;
      effective_cap_usd: number;
      remaining_usd: number;
      blocked: boolean;
    }>(`/llm/epoch/spend/${projectId}/override`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // ── Shared DB Config ─────────────────────────────
  getSourceDB: (projectId: string) =>
    request<DBConfigOut | null>(`/projects/${projectId}/source-db`),

  setSourceDB: (projectId: string, data: ProjectDbConfigInput) =>
    request<DBConfigOut>(`/projects/${projectId}/source-db`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteSourceDB: (projectId: string) =>
    request<void>(`/projects/${projectId}/source-db`, { method: "DELETE" }),

  // A project can hold any number of saved source/target connections (any
  // db_type mix) — this is the list/create/delete trio any agent's "use an
  // existing connection" picker reads from, additive to the legacy
  // single-slot get/set/delete above (which still work unchanged, reading
  // the most-recently-created entry).
  listSourceConnections: (projectId: string) =>
    request<DBConfigOut[]>(`/projects/${projectId}/source-connections`),

  // The connector capability registry — which db types (and which extra
  // fields) `agentKey` is wired for today. Omit `agentKey` for the union
  // across every agent (the cross-agent connections-management view).
  listSupportedConnectors: (agentKey?: string, includeFileSources = false) => {
    const params = new URLSearchParams();
    if (agentKey) params.set("agent", agentKey);
    if (includeFileSources) params.set("include_file_sources", "true");
    const qs = params.toString();
    return request<APIEnvelopeV2<{ connectors: ConnectorOut[] }>>(
      `/connectors/supported${qs ? `?${qs}` : ""}`,
    ).then((env) => env.data.connectors);
  },

  createSourceConnection: (
    projectId: string,
    data: {
      db_type: string;
      name?: string;
      host?: string;
      port?: number;
      database?: string;
      schema_name?: string;
      username?: string;
      password?: string;
      auth_type?: string;
      account?: string;
      warehouse?: string;
      role?: string;
      additional_config?: Record<string, unknown>;
    },
  ) =>
    request<DBConfigOut>(`/projects/${projectId}/source-connections`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  deleteSourceConnection: (projectId: string, connectionId: string) =>
    request<void>(`/projects/${projectId}/source-connections/${connectionId}`, {
      method: "DELETE",
    }),

  renameSourceConnection: (
    projectId: string,
    connectionId: string,
    name: string,
  ) =>
    request<DBConfigOut>(
      `/projects/${projectId}/source-connections/${connectionId}`,
      { method: "PATCH", body: JSON.stringify({ name }) },
    ),

  // Fixes a saved connection's stored credential — the recovery path for
  // "this saved connection's stored password could not be found" (e.g. an
  // ephemeral dev Vault got reset). Used by ConnectDatabaseDialog's inline
  // "re-enter the password" prompt on a failed saved-connection attempt.
  updateSourceConnectionSecret: (
    projectId: string,
    connectionId: string,
    updates: { password?: string; clientSecret?: string },
  ) =>
    request<DBConfigOut>(
      `/projects/${projectId}/source-connections/${connectionId}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          ...(updates.password !== undefined
            ? { password: updates.password }
            : {}),
          ...(updates.clientSecret !== undefined
            ? { client_secret: updates.clientSecret }
            : {}),
        }),
      },
    ),

  getTargetDB: (projectId: string) =>
    request<DBConfigOut | null>(`/projects/${projectId}/target-db`),

  setTargetDB: (projectId: string, data: ProjectDbConfigInput) =>
    request<DBConfigOut>(`/projects/${projectId}/target-db`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteTargetDB: (projectId: string) =>
    request<void>(`/projects/${projectId}/target-db`, { method: "DELETE" }),

  listTargetConnections: (projectId: string) =>
    request<DBConfigOut[]>(`/projects/${projectId}/target-connections`),

  createTargetConnection: (
    projectId: string,
    data: {
      db_type: string;
      name?: string;
      host?: string;
      port?: number;
      database?: string;
      schema_name?: string;
      username?: string;
      password?: string;
      auth_type?: string;
      account?: string;
      warehouse?: string;
      role?: string;
      additional_config?: Record<string, unknown>;
    },
  ) =>
    request<DBConfigOut>(`/projects/${projectId}/target-connections`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  deleteTargetConnection: (projectId: string, connectionId: string) =>
    request<void>(`/projects/${projectId}/target-connections/${connectionId}`, {
      method: "DELETE",
    }),

  renameTargetConnection: (
    projectId: string,
    connectionId: string,
    name: string,
  ) =>
    request<DBConfigOut>(
      `/projects/${projectId}/target-connections/${connectionId}`,
      { method: "PATCH", body: JSON.stringify({ name }) },
    ),

  // Fixes a saved connection's stored credential — see
  // updateSourceConnectionSecret's comment for the "why".
  updateTargetConnectionSecret: (
    projectId: string,
    connectionId: string,
    updates: { password?: string; clientSecret?: string },
  ) =>
    request<DBConfigOut>(
      `/projects/${projectId}/target-connections/${connectionId}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          ...(updates.password !== undefined
            ? { password: updates.password }
            : {}),
          ...(updates.clientSecret !== undefined
            ? { client_secret: updates.clientSecret }
            : {}),
        }),
      },
    ),

  // ── Engagement config (engagement_slug + dq_rule_packs) ──────────
  listEngagements: () => request<EngagementInfo[]>(`/engagements`),

  getEngagementConfig: (projectId: string) =>
    request<EngagementConfigOut>(`/projects/${projectId}/engagement-config`),

  setEngagementConfig: (
    projectId: string,
    data: { engagement_slug?: string; dq_rule_packs?: string[] },
  ) =>
    request<EngagementConfigOut>(`/projects/${projectId}/engagement-config`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  testDBConnection: (projectId: string, data: ProjectDbConfigInput) =>
    request<{ success: boolean; message: string }>(
      `/projects/${projectId}/test-db-connection`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    ),

  getSharedConfig: (projectId: string) =>
    request<{
      project_id: string;
      source_db: DBConfigOut | null;
      target_db: DBConfigOut | null;
      llm: LLMConfigOut | null;
    }>(`/projects/${projectId}/shared-config`),

  // ── Orchestrator (autonomous mode) ───────────────
  createOrchestratorPlan: (
    projectId: string,
    goal: string,
    documentIds: string[] = [],
  ) =>
    request<OrchestratorPlanOut>(`/projects/${projectId}/orchestrator/plan`, {
      method: "POST",
      body: JSON.stringify({ goal, document_ids: documentIds }),
    }),

  getOrchestratorPlan: (projectId: string) =>
    request<OrchestratorPlanOut | null>(
      `/projects/${projectId}/orchestrator/plan`,
    ),

  answerOrchestratorQuestion: (
    projectId: string,
    stepId: string,
    answer: string,
  ) =>
    request<OrchestratorPlanOut>(`/projects/${projectId}/orchestrator/answer`, {
      method: "POST",
      body: JSON.stringify({ step_id: stepId, answer }),
    }),

  runOrchestratorStep: (projectId: string, stepId: string) =>
    request<OrchestratorPlanOut>(
      `/projects/${projectId}/orchestrator/step/${stepId}/run`,
      {
        method: "POST",
      },
    ),

  completeOrchestratorStep: (projectId: string, stepId: string) =>
    request<OrchestratorPlanOut>(
      `/projects/${projectId}/orchestrator/step/${stepId}/complete`,
      {
        method: "POST",
      },
    ),

  approveOrchestratorStep: (
    projectId: string,
    stepId: string,
    approved: boolean,
    feedback = "",
  ) =>
    request<OrchestratorPlanOut>(
      `/projects/${projectId}/orchestrator/step/${stepId}/approve`,
      {
        method: "POST",
        body: JSON.stringify({ approved, feedback }),
      },
    ),

  // Reject MUST hit the /reject endpoint (not /approve) — the backend
  // approve handler always flips the step to `approved` regardless of the
  // body, so routing a rejection through it silently approves the step.
  // The rejection reason is the gate's `notes` (required by the HITL
  // service), so send it under that key.
  rejectOrchestratorStep: (projectId: string, stepId: string, notes: string) =>
    request<OrchestratorPlanOut>(
      `/projects/${projectId}/orchestrator/step/${stepId}/reject`,
      {
        method: "POST",
        body: JSON.stringify({ notes }),
      },
    ),

  skipOrchestratorStep: (projectId: string, stepId: string) =>
    request<OrchestratorPlanOut>(
      `/projects/${projectId}/orchestrator/step/${stepId}/skip`,
      {
        method: "POST",
      },
    ),

  updateStepOutput: (
    projectId: string,
    stepId: string,
    outputSummary: string,
    outputArtifacts: string,
  ) =>
    request<OrchestratorPlanOut>(
      `/projects/${projectId}/orchestrator/step/${stepId}/output`,
      {
        method: "PUT",
        body: JSON.stringify({
          output_summary: outputSummary,
          output_artifacts: outputArtifacts,
        }),
      },
    ),

  cancelOrchestratorPlan: (projectId: string) =>
    request<OrchestratorPlanOut>(`/projects/${projectId}/orchestrator/cancel`, {
      method: "POST",
    }),

  // ── Orchestrator Documents ───────────────────────
  uploadOrchestratorDocument: async (
    projectId: string,
    file: File,
  ): Promise<OrchestratorDocumentOut> => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(
      `${BASE}/projects/${projectId}/orchestrator/documents`,
      {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token":
            document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
        },
        credentials: "include",
      },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        formatApiErrorDetail(body.detail, `Upload failed: ${res.status}`),
      );
    }
    return res.json();
  },

  listOrchestratorDocuments: (projectId: string) =>
    request<OrchestratorDocumentOut[]>(
      `/projects/${projectId}/orchestrator/documents`,
    ),

  // ── MCP bearer tokens (P4.5b) ────────────────────
  listMCPTokens: () => request<MCPTokenView[]>("/mcp-tokens"),
  listMCPKnownSkills: () =>
    request<{ skills: string[] }>("/mcp-tokens/known-skills"),
  issueMCPToken: (body: { consumer_id: string; scopes: string[] }) =>
    request<MCPTokenIssueResponse>("/mcp-tokens", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  revokeMCPToken: (tokenId: string) =>
    request<void>(`/mcp-tokens/${tokenId}`, { method: "DELETE" }),

  // ── Outputs / Review inbox ──────────────────────────────
  /** GET /api/v2/outputs — the tenant's recorded review artifacts. */
  listOutputs: () =>
    request<{ artifacts: OutputArtifact[] }>("/outputs").then(
      (r) => r.artifacts,
    ),

  /** Download an artifact through the app (stays inside the ZTD boundary). */
  downloadOutput: async (artifactId: string, filename: string) => {
    const res = await fetch(`${BASE}/outputs/${artifactId}/download`, {
      credentials: "include",
    });
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        formatApiErrorDetail(body.detail, `Download failed: ${res.status}`),
      );
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // ── Data Converter — legacy copybook/ADABAS flow ───────────────────────
  converterDemoScenario: () =>
    request<ConverterDemoScenario>("/data-converter/demo-scenario"),

  converterParseCopybook: (copybookText: string) =>
    request<{ fields: ConverterSourceField[] }>(
      "/data-converter/copybook/parse",
      {
        method: "POST",
        body: JSON.stringify({ copybook_text: copybookText }),
      },
    ),

  converterPreview: (task: ConverterTask, maxRows = 25) =>
    request<ConverterPreviewResponse>("/data-converter/preview", {
      method: "POST",
      body: JSON.stringify({ task, max_rows: maxRows }),
    }),

  converterLoad: (task: ConverterTask, connectionRef: ConverterConnectionRef) =>
    request<ConverterLoadResponse>("/data-converter/load", {
      method: "POST",
      body: JSON.stringify({
        task,
        ...converterConnRefFields("", connectionRef),
      }),
    }),

  // Bulk variant for the File Source flow's multi-file upload — loads N
  // tasks (one per uploaded file, all sharing one target connection)
  // CONCURRENTLY in a single request rather than one /load round-trip per
  // file. A per-item failure is reported inline (result[i].error) without
  // aborting the rest of the batch.
  converterLoadBatch: (
    tasks: ConverterTask[],
    connectionRef: ConverterConnectionRef,
  ) =>
    request<{ results: (ConverterLoadResponse & { error?: string })[] }>(
      "/data-converter/file/load-batch",
      {
        method: "POST",
        body: JSON.stringify({
          items: tasks.map((task) => ({
            task,
            ...converterConnRefFields("", connectionRef),
          })),
        }),
      },
    ),

  // Async counterpart to converterLoadBatch — starts a real Celery job
  // (mirrors the DB-to-DB /data/load job type) and returns a job_id
  // immediately instead of blocking on every file. Poll with
  // converterLoadDataStatus (same endpoint/shape /data/load uses) —
  // table_results is appended to as each file finishes, so a poller sees
  // real incremental progress instead of an all-or-nothing response. One
  // connection for the whole batch (not per-item, unlike converterLoadBatch).
  // `stages` (groups of 0-based indices into `tasks`) mirrors the DB-to-DB
  // load's load-order concurrency: tasks within a stage run concurrently
  // server-side, stages run one after another. Omit for fully sequential.
  converterLoadFileBatchAsync: (
    tasks: ConverterTask[],
    connectionRef: ConverterConnectionRef,
    stages?: number[][],
  ) =>
    request<DataLoadStartResponse>("/data-converter/file/load-batch-async", {
      method: "POST",
      body: JSON.stringify({
        tasks,
        stages,
        ...converterConnRefFields("", connectionRef),
      }),
    }),

  // ── Data Converter — File Source flow ──────────────────────────────────
  converterUploadFile: async (
    file: File,
    projectId?: string,
  ): Promise<ConverterFileUploadResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    if (projectId) formData.append("project_id", projectId);
    const res = await fetch(`${BASE}/data-converter/file/upload`, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        formatApiErrorDetail(body.detail, `File upload failed: ${res.status}`),
      );
    }
    return res.json();
  },

  converterParseLayout: (
    layoutText: string,
    layoutFormat: "cobol" | "simple" | "pyspark" = "cobol",
  ) =>
    request<ConverterLayoutParseResponse>("/data-converter/layout/parse", {
      method: "POST",
      body: JSON.stringify({
        layout_text: layoutText,
        layout_format: layoutFormat,
      }),
    }),

  // "Import from SFTP" -- request-scoped connect/list/import for the File
  // Source flow's remote-server entry point; import returns the exact same
  // {file_id, file_path, filename, size_bytes} shape as converterUploadFile.
  converterSftpConnect: (connection: ConverterSftpConnectionConfig) =>
    request<{ status: string; host: string; server_version: string }>(
      "/data-converter/file/sftp/connect",
      { method: "POST", body: JSON.stringify({ connection }) },
    ),

  converterSftpListFiles: (
    connection: ConverterSftpConnectionConfig,
    remotePath?: string,
  ) =>
    request<{ path: string; files: ConverterSftpFileMeta[] }>(
      "/data-converter/file/sftp/list-files",
      {
        method: "POST",
        body: JSON.stringify({ connection, remote_path: remotePath }),
      },
    ),

  converterSftpImportFile: (
    connection: ConverterSftpConnectionConfig,
    remoteFilePath: string,
    projectId?: string,
  ) =>
    request<ConverterFileUploadResponse>(
      "/data-converter/file/sftp/import-file",
      {
        method: "POST",
        body: JSON.stringify({
          connection,
          remote_file_path: remoteFilePath,
          // The project the imported file belongs to (any member may use it);
          // omitted, it belongs to the importer alone.
          ...(projectId ? { project_id: projectId } : {}),
        }),
      },
    ),

  // SFTP counterpart to converterParseLegacySourceBatch -- downloads one or
  // more remote files and parses them into the same SchemaExtractResult
  // shape a local Legacy Code upload produces, so SFTP is a Legacy Code
  // source-type acquisition method (Source -> Target Mapping) rather than
  // its own separate Data Loader flow. `consolidate` behaves exactly like
  // converterParseLegacySourceBatch's flag -- downloaded files funnel
  // through the identical pipeline.
  converterParseLegacySftp: (
    connection: ConverterSftpConnectionConfig,
    remoteFilePaths: string[],
    projectId?: string,
    consolidate = true,
  ) =>
    request<SchemaExtractResult>("/data-converter/source/parse-legacy-sftp", {
      method: "POST",
      body: JSON.stringify({
        connection,
        remote_file_paths: remoteFilePaths,
        project_id: projectId,
        consolidate,
      }),
    }),

  // "Import from SharePoint" -- request-scoped connect/list/import for the
  // File Source flow's SharePoint List entry point; import returns the
  // exact same {file_id, file_path, filename, size_bytes} shape as
  // converterUploadFile (list items are written out as a CSV).
  converterSharePointConnect: (
    connection: ConverterSharePointConnectionConfig,
  ) =>
    request<{ site_id: string; list_count: number }>(
      "/data-converter/file/sharepoint/connect",
      { method: "POST", body: JSON.stringify({ connection }) },
    ),

  converterSharePointListLists: (
    connection: ConverterSharePointConnectionConfig,
  ) =>
    request<{ lists: ConverterSharePointListMeta[] }>(
      "/data-converter/file/sharepoint/lists",
      { method: "POST", body: JSON.stringify({ connection }) },
    ),

  // SharePoint counterpart to converterParseLegacySourceBatch -- one or
  // more SharePoint Lists parsed into the same SchemaExtractResult shape a
  // local Legacy Code upload produces, so SharePoint is a Legacy Code
  // source-type acquisition method (Source -> Target Mapping) rather than
  // its own separate Data Loader flow.
  converterParseLegacySharePoint: (
    connection: ConverterSharePointConnectionConfig,
    listIds: string[],
    filenames?: string[],
    projectId?: string,
  ) =>
    request<SchemaExtractResult>(
      "/data-converter/source/parse-legacy-sharepoint",
      {
        method: "POST",
        body: JSON.stringify({
          connection,
          list_ids: listIds,
          filenames,
          project_id: projectId,
        }),
      },
    ),

  // "Extract Tables from PDF" -- the standalone Data Loader entry point:
  // runs deterministic (non-LLM) table detection over an already-uploaded
  // PDF (see converterUploadFile); each detected table is registered as
  // its own File Source (CSV), so a multi-table PDF yields multiple
  // selectable entries.
  converterPdfExtractTables: (filePath: string) =>
    request<{ tables: ConverterPdfExtractedTable[] }>(
      "/data-converter/file/pdf/extract-tables",
      { method: "POST", body: JSON.stringify({ file_path: filePath }) },
    ),

  // Same as converterPdfExtractTables but for several PDFs expected to
  // share the same shape of information -- merges them into one
  // consolidated dataset (with a "Source_File" column) when every file
  // yields exactly one matching-columns table, else falls back to one
  // entry per detected table (plus per-file error messages) so the caller
  // can still pick tables individually.
  converterPdfExtractTablesMulti: (filePaths: string[]) =>
    request<ConverterPdfExtractTablesMultiResponse>(
      "/data-converter/file/pdf/extract-tables-multi",
      { method: "POST", body: JSON.stringify({ file_paths: filePaths }) },
    ),

  // PDF counterpart to converterParseLegacySourceBatch -- extracts every
  // table from one or more already-uploaded PDFs (see converterUploadFile)
  // and parses them into the same SchemaExtractResult shape a local Legacy
  // Code upload produces, so PDF table extraction is a Legacy Code
  // source-type acquisition method (Source -> Target Mapping) rather than
  // its own separate Data Loader flow. When `consolidate` is true
  // (default) and two or more PDFs share the same single-table layout,
  // their rows are consolidated into one source table (with an added
  // "Source_File" column) instead of one per file -- reported via
  // schemaResult.consolidation_note. Pass consolidate=false to always get
  // one source table per detected table instead (the regular per-file
  // STTM flow), regardless of layout similarity. See
  // converterPreviewPdfTables to sanity-check extraction first.
  // Deterministic (pdfplumber) -- no LLM call is made.
  converterParseLegacyPdf: (
    filePaths: string[],
    projectId?: string,
    consolidate = true,
  ) =>
    request<SchemaExtractResult>("/data-converter/source/parse-legacy-pdf", {
      method: "POST",
      body: JSON.stringify({
        file_paths: filePaths,
        project_id: projectId,
        consolidate,
      }),
    }),

  // Same as converterParseLegacyPdf but offloaded to a Celery worker instead
  // of running OCR/table-detection in-process on the API -- a large or
  // scanned-heavy batch can spike memory and take tens of seconds to a few
  // minutes. Returns immediately with a job_id; poll
  // converterParseLegacyPdfStatus until status is no longer "running".
  converterParseLegacyPdfAsync: (
    filePaths: string[],
    projectId?: string,
    consolidate = true,
  ) =>
    request<{ job_id: string }>(
      "/data-converter/source/parse-legacy-pdf-async",
      {
        method: "POST",
        body: JSON.stringify({
          file_paths: filePaths,
          project_id: projectId,
          consolidate,
        }),
      },
    ),

  converterParseLegacyPdfStatus: (jobId: string) =>
    request<{
      job_id: string;
      status: "running" | "completed" | "failed";
      result: (SchemaExtractResult & { warnings?: string[] }) | null;
      errors: string[];
    }>(`/data-converter/source/parse-legacy-pdf-status/${jobId}`),

  // Read-only sanity-check preview of what pdfplumber extracts from one or
  // more already-uploaded PDFs -- columns plus a capped sample of rows per
  // detected table -- without persisting or registering anything. Lets the
  // user verify extraction looks right before "Parse & Continue".
  converterPreviewPdfTables: (filePaths: string[]) =>
    request<ConverterPdfPreviewResponse>("/data-converter/file/pdf/preview", {
      method: "POST",
      body: JSON.stringify({ file_paths: filePaths }),
    }),

  // Auto-detects columns for an already-uploaded CSV/TSV/JSON/JSONL/Parquet
  // File Source — these formats are self-describing, so they skip the
  // manual Record Layout (start/length) step that only fixed-width
  // extracts genuinely need.
  converterInspectFile: (params: {
    file_path: string;
    format_type: Exclude<ConverterSourceFormat["format_type"], "fixed_width">;
    encoding: string;
    has_header: boolean;
  }) =>
    request<ConverterLayoutParseResponse>("/data-converter/file/inspect", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  converterBuildFileTask: (params: {
    file_path: string;
    format_type: ConverterSourceFormat["format_type"];
    encoding: string;
    has_header: boolean;
    fields: ConverterSourceField[];
    target_table: string;
    target_schema: string;
    if_exists: ConverterTarget["if_exists"];
    chunk_size: number;
  }) =>
    request<ConverterBuildFileTaskResponse>("/data-converter/file/build-task", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  converterTestFileConnection: (connectionRef: ConverterConnectionRef) =>
    request<ConverterTestConnectionResponse>(
      "/data-converter/file/test-connection",
      {
        method: "POST",
        body: JSON.stringify(converterConnRefFields("", connectionRef)),
      },
    ),

  // Folder/multi-file bulk load with real parallel processing (one
  // independent Celery task per file, picked up by any worker container)
  // and live progress via the pipelines engine — the async counterpart to
  // /file/load-batch-async's single-sequential-task job, for volumes where
  // that would be too slow. Same request shape: one connection, N per-file
  // tasks (each task's own source.path, same shared target table).
  converterLoadBatchPipeline: (
    tasks: ConverterTask[],
    connectionRef: ConverterConnectionRef,
  ) =>
    request<{ pipeline_run_id: string; file_count: number }>(
      "/data-converter/file/load-batch-pipeline",
      {
        method: "POST",
        body: JSON.stringify({
          tasks,
          ...converterConnRefFields("", connectionRef),
        }),
      },
    ),

  // ── Data Converter — RDS source + GovConnect STTM ──────────────────────
  // Either `connection` (a fresh manual connect) or `connectionId` +
  // `projectId` (reuse one of the project's saved connections — resolved
  // server-side, see ztd_app's admin/project_db_config.resolve_connect_config).
  converterSourceConnect: (params: {
    connection?: DbConnectionConfig;
    connectionId?: string;
    projectId?: string;
    // User-chosen label for a fresh `connection` when it gets auto-saved to
    // the project's shared connection library — ignored when connectionId
    // is set (reusing a saved connection keeps its existing name).
    connectionName?: string;
  }) =>
    request<{
      status: string;
      schemas: string[];
      connection_id?: string;
      schema_name?: string;
    }>("/data-converter/source/connect", {
      method: "POST",
      body: JSON.stringify({
        connection: params.connection,
        connection_id: params.connectionId,
        project_id: params.projectId,
        connection_name: params.connectionName,
      }),
    }),

  converterSourceScan: (
    connectionRef: ConverterConnectionRef,
    schemaName?: string,
    tableNames?: string[],
  ) =>
    request<SchemaExtractResult>("/data-converter/source/scan", {
      method: "POST",
      body: JSON.stringify({
        ...converterConnRefFields("", connectionRef),
        ...(schemaName ? { schema_name: schemaName } : {}),
        ...(tableNames && tableNames.length > 0
          ? { table_names: tableNames }
          : {}),
      }),
    }),

  // Same shape as converterSourceScan, for a target that's a plain RDBMS
  // rather than a CRM platform.
  converterTargetScan: (
    connectionRef: ConverterConnectionRef,
    schemaName?: string,
  ) =>
    request<SchemaExtractResult>("/data-converter/target/scan", {
      method: "POST",
      body: JSON.stringify({
        ...converterConnRefFields("", connectionRef),
        ...(schemaName ? { schema_name: schemaName } : {}),
      }),
    }),

  // "Generate from Legacy Code" entry point — uploads a piece of legacy
  // code/schema (COBOL copybook, SQL DDL, JSON, XML, Parquet) and returns
  // the same SchemaExtractResult shape a live database scan produces, so it
  // flows through the existing Schema DDL / Target Platform / Mapping /
  // STTM pipeline unchanged.
  converterParseLegacySource: async (
    file: File,
    projectId?: string,
  ): Promise<SchemaExtractResult> => {
    const formData = new FormData();
    formData.append("file", file);
    if (projectId) formData.append("project_id", projectId);
    const res = await fetch(`${BASE}/data-converter/source/parse-legacy`, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        body.detail || `Legacy source parse failed: ${res.status}`,
      );
    }
    return res.json();
  },

  // "Import Target Model" entry point (Target Platform step, RDBMS mode) —
  // same parser as converterParseLegacySource, but snapshotted server-side
  // as this project's "target" schema instead of "source". Used when the
  // target database doesn't have the desired tables yet (nothing to scan
  // via converterTargetScan), and their structure instead comes from an
  // existing DDL script or schema export that should be created verbatim.
  converterParseLegacyTarget: async (
    file: File,
    projectId?: string,
  ): Promise<SchemaExtractResult> => {
    const formData = new FormData();
    formData.append("file", file);
    if (projectId) formData.append("project_id", projectId);
    const res = await fetch(`${BASE}/data-converter/target/parse-legacy`, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        body.detail || `Legacy target parse failed: ${res.status}`,
      );
    }
    return res.json();
  },

  // Bulk variant — uploads several legacy code/schema files (or every file
  // picked from a folder) in one call and merges them into a single
  // multi-table SchemaExtractResult, so a source whose metadata is spread
  // across multiple files can be crawled and mapped in one pass. When
  // `consolidate` is true (default) and two or more CSV/TSV/JSON/Parquet
  // files share the same columns (e.g. a batch of monthly exports of the
  // same table), their rows are consolidated into a SINGLE source table
  // instead of one per file -- reported via schemaResult.consolidation_note,
  // same as the PDF acquisition method. Pass consolidate=false to always get
  // one source table per file/table instead (independent mapping),
  // regardless of structure overlap.
  converterParseLegacySourceBatch: async (
    files: File[],
    projectId?: string,
    consolidate = true,
  ): Promise<SchemaExtractResult> => {
    const formData = new FormData();
    for (const file of files) formData.append("files", file);
    if (projectId) formData.append("project_id", projectId);
    formData.append("consolidate", String(consolidate));
    const res = await fetch(
      `${BASE}/data-converter/source/parse-legacy-batch`,
      {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token":
            document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
        },
        credentials: "include",
      },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        body.detail || `Legacy source batch parse failed: ${res.status}`,
      );
    }
    return res.json();
  },

  // Parses one or more uploaded SAP BODS <DataIntegratorExport> dataflow
  // XML files (real ETL job/workflow/dataflow definitions, not just table
  // schema) directly into an STTM mapping plus source/target DDL and an
  // audit workbook -- no separate "Generate Schema" / mapping-review step,
  // since the BODS XML itself already determines the mapping.
  //
  // A large scanned BODS export (or, once wired up, an OCR-fallback PDF
  // path) can take a long time server-side with no incremental progress
  // to show -- unlike the codebase's async-job-plus-polling pattern (e.g.
  // reportsV2.waitForReport), this is still a single synchronous request,
  // so a client-side timeout is the minimum needed to keep the UI spinner
  // from running indefinitely with no way to detect a stuck request.
  converterParseBodsDataflowBatch: async (
    files: File[],
    projectName: string,
    projectId?: string,
  ): Promise<ConverterBodsDataflowImportResponse> => {
    const formData = new FormData();
    for (const file of files) formData.append("files", file);
    formData.append("project_name", projectName);
    if (projectId) formData.append("project_id", projectId);
    let res: Response;
    try {
      res = await fetch(
        `${BASE}/data-converter/source/parse-bods-dataflow-batch`,
        {
          method: "POST",
          body: formData,
          headers: {
            "X-CSRF-Token":
              document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
          },
          credentials: "include",
          signal: AbortSignal.timeout(LEGACY_IMPORT_TIMEOUT_MS),
        },
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === "TimeoutError") {
        throw new Error(
          "BODS dataflow import timed out -- the file(s) may be too large or the server is unresponsive.",
        );
      }
      throw err;
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        body.detail || `BODS dataflow import failed: ${res.status}`,
      );
    }
    return res.json();
  },

  // Parses one or more uploaded Informatica PowerCenter mapping-export
  // XML files (real <POWERMART> Source Qualifier/Expression/Joiner/Lookup/
  // Filter transformation logic, not just table schema) directly into an
  // STTM mapping plus source/target DDL and an audit workbook -- same
  // scope/shape as converterParseBodsDataflowBatch, for Informatica's own
  // export format.
  converterParseInformaticaMappingBatch: async (
    files: File[],
    projectName: string,
    projectId?: string,
  ): Promise<ConverterInformaticaMappingImportResponse> => {
    const formData = new FormData();
    for (const file of files) formData.append("files", file);
    formData.append("project_name", projectName);
    if (projectId) formData.append("project_id", projectId);
    let res: Response;
    try {
      res = await fetch(
        `${BASE}/data-converter/source/parse-informatica-mapping-batch`,
        {
          method: "POST",
          body: formData,
          headers: {
            "X-CSRF-Token":
              document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
          },
          credentials: "include",
          signal: AbortSignal.timeout(LEGACY_IMPORT_TIMEOUT_MS),
        },
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === "TimeoutError") {
        throw new Error(
          "Informatica mapping import timed out -- the file(s) may be too large or the server is unresponsive.",
        );
      }
      throw err;
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        body.detail || `Informatica mapping import failed: ${res.status}`,
      );
    }
    return res.json();
  },

  converterSalesforceConnect: (
    connection: SalesforceConnectionConfig,
    projectId?: string,
  ) =>
    request<{
      status: string;
      org_id: string;
      org_name: string;
      org_type: string;
      object_count: number;
    }>("/data-converter/salesforce/connect", {
      method: "POST",
      body: JSON.stringify({ connection, project_id: projectId ?? null }),
    }),

  converterSalesforceObjects: (
    connection: SalesforceConnectionConfig,
    objectNames?: string[],
    includeCustom = true,
    projectId?: string,
  ) =>
    request<{ object_count: number; objects: SalesforceObjectMeta[] }>(
      "/data-converter/salesforce/objects",
      {
        method: "POST",
        body: JSON.stringify({
          connection,
          object_names: objectNames ?? null,
          include_custom: includeCustom,
          project_id: projectId ?? null,
        }),
      },
    ),

  // CRM/SaaS Systems counterpart to converterParseLegacySourceBatch --
  // extracts records from one or more Salesforce/Dynamics 365/Power
  // Platform/Workday objects (connection.target_type selects the
  // connector, same as converterSalesforceConnect/Objects above) and
  // parses them into the same SchemaExtractResult shape a local CSV
  // upload would produce, so CRM/SaaS Systems is a Legacy Code
  // source-type acquisition method (Source -> Target Mapping) rather than
  // its own separate Data Loader flow -- lets a system like Salesforce or
  // Workday be extracted FROM (not just written to, as the Target
  // Platform Load step already does).
  converterParseLegacyCrm: (
    connection: SalesforceConnectionConfig,
    objectNames: string[],
    projectId?: string,
    // Opt-in bounded preview sample (issue #735 step 2) -- schema/mapping
    // preview only, defaults false to preserve the existing full-object
    // extraction (still the only way to migrate a whole object through
    // this generic File-Source + Load Data path; see
    // crm_objects_as_legacy_source's docstring for the full-migration path).
    sampleOnly?: boolean,
    sampleSize?: number,
  ) =>
    request<SchemaExtractResult>("/data-converter/source/parse-legacy-crm", {
      method: "POST",
      body: JSON.stringify({
        connection,
        object_names: objectNames,
        project_id: projectId ?? null,
        sample_only: sampleOnly ?? false,
        ...(sampleSize !== undefined ? { sample_size: sampleSize } : {}),
      }),
    }),

  converterGenerateMapping: (
    sourceSchema: SchemaExtractResult,
    sfObjects: SalesforceObjectMeta[],
    llmEnhance = true,
    pairs?: { source_table: string; target_object: string }[],
    projectId?: string,
  ) =>
    request<{ mapping_count: number; mappings: TableObjectMapping[] }>(
      "/data-converter/mapping/generate",
      {
        method: "POST",
        body: JSON.stringify({
          source_schema: sourceSchema,
          sf_objects: sfObjects,
          llm_enhance: llmEnhance,
          ...(pairs && pairs.length ? { pairs } : {}),
          project_id: projectId ?? null,
        }),
      },
    ),

  converterGenerateComplexMapping: (
    sourceSchema: SchemaExtractResult,
    sfObjects: SalesforceObjectMeta[],
    groups: ComplexMappingGroup[],
    projectId?: string,
  ) =>
    request<{ mapping_count: number; mappings: TableObjectMapping[] }>(
      "/data-converter/mapping/generate-complex",
      {
        method: "POST",
        body: JSON.stringify({
          source_schema: sourceSchema,
          sf_objects: sfObjects,
          groups: groups.map((g) => ({
            source_tables: g.sourceTables,
            joins: g.joins,
            target_object: g.targetObject,
            ...(g.includedColumns
              ? { included_columns: g.includedColumns }
              : {}),
            ...(g.tableConnectionIds
              ? { table_connection_ids: g.tableConnectionIds }
              : {}),
            ...(g.tableSchemas ? { table_schemas: g.tableSchemas } : {}),
          })),
          project_id: projectId ?? null,
        }),
      },
    ),

  converterGenerateSttm: (
    mappings: TableObjectMapping[],
    sourceSchema?: SchemaExtractResult | null,
    sfObjects?: SalesforceObjectMeta[] | null,
    projectName = "Data Migration",
    projectId?: string,
    // Saved source connection id -> human-readable label, so a
    // heterogeneous-join mapping's downloaded STTM workbook can show which
    // physical source system each row actually came from (see
    // TableObjectMapping.join_table_connection_ids).
    connectionLabels?: Record<string, string> | null,
  ) =>
    request<SttmGenerateResponse>("/data-converter/sttm/generate", {
      method: "POST",
      body: JSON.stringify({
        mappings,
        source_schema: sourceSchema ?? null,
        sf_objects: sfObjects ?? null,
        project_name: projectName,
        project_id: projectId ?? null,
        connection_labels: connectionLabels ?? null,
      }),
    }),

  // 2026-09-02, requested by user: on Finalize, generates the same .xlsx
  // workbook "Generate STTM" produces, but ALSO persists a reference to it
  // on the project's mapping row so Load Data/Load to Target/Reconciliation
  // (and a resumed session) can download or replace it later without
  // regenerating from scratch.
  converterGenerateProjectMappingFile: (
    projectId: string,
    mappings: TableObjectMapping[],
    sourceSchema?: SchemaExtractResult | null,
    sfObjects?: SalesforceObjectMeta[] | null,
    projectName = "Data Migration",
    connectionLabels?: Record<string, string> | null,
  ) =>
    request<ProjectMappingFileOut>(
      `/data-converter/project/${projectId}/mapping/xlsx`,
      {
        method: "POST",
        body: JSON.stringify({
          mappings,
          source_schema: sourceSchema ?? null,
          sf_objects: sfObjects ?? null,
          project_name: projectName,
          connection_labels: connectionLabels ?? null,
        }),
      },
    ),

  converterGetProjectMappingFile: (projectId: string) =>
    request<ProjectMappingFileOut>(
      `/data-converter/project/${projectId}/mapping/xlsx`,
    ),

  // STTM review's "Finalize" action — generates a Data Dictionary workbook
  // (one row per source column) alongside the mapping, enriched with
  // Metadata Curator / Data Profiling context when those integration
  // skills are enabled for the project.
  converterGenerateDataDictionary: (
    mappings: TableObjectMapping[],
    sourceSchema?: SchemaExtractResult | null,
    projectName = "Data Migration",
    projectId?: string,
  ) =>
    request<DataDictionaryGenerateResponse>(
      "/data-converter/data-dictionary/generate",
      {
        method: "POST",
        body: JSON.stringify({
          mappings,
          source_schema: sourceSchema ?? null,
          project_name: projectName,
          project_id: projectId ?? null,
        }),
      },
    ),

  converterImportTransformRules: async (
    mappings: TableObjectMapping[],
    file: File,
  ): Promise<TransformRulesImportResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    appendJsonFile(formData, "mappings_file", mappings);
    const res = await fetch(
      `${BASE}/data-converter/sttm/import-transform-rules`,
      {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token":
            document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
        },
        credentials: "include",
      },
    );
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        formatApiErrorDetail(
          body.detail,
          `Import transform rules failed: ${res.status}`,
        ),
      );
    }
    return res.json();
  },

  converterApplyRuleDecisions: async (
    mappings: TableObjectMapping[],
    decisions: TransformRuleDecision[],
  ): Promise<ApplyRuleDecisionsResponse> => {
    const formData = new FormData();
    appendJsonFile(formData, "mappings_file", mappings);
    appendJsonFile(formData, "decisions_file", decisions);
    const res = await fetch(
      `${BASE}/data-converter/sttm/apply-rule-decisions`,
      {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token":
            document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
        },
        credentials: "include",
      },
    );
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        formatApiErrorDetail(
          body.detail,
          `Apply DQ rule decisions failed: ${res.status}`,
        ),
      );
    }
    return res.json();
  },

  // ── Legacy transformation-rule import (Excel/SQL/PL-SQL/SSIS/Informatica/BODS) ──
  // Parses an uploaded legacy artifact into candidate transformation rules,
  // auto-maps them against the current STTM, and persists the batch as an
  // audit-trail entry pending review — nothing is applied to the STTM yet.
  converterImportLegacyRules: async (
    mappings: TableObjectMapping[],
    file: File,
    opts: { projectId?: string; artifactType?: LegacyArtifactType } = {},
  ): Promise<LegacyRuleBatch> => {
    const formData = new FormData();
    formData.append("file", file);
    appendJsonFile(formData, "mappings_file", mappings);
    if (opts.projectId) formData.append("project_id", opts.projectId);
    if (opts.artifactType) formData.append("artifact_type", opts.artifactType);
    const res = await fetch(`${BASE}/data-converter/legacy-rules/import`, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        formatApiErrorDetail(
          body.detail,
          `Legacy rule import failed: ${res.status}`,
        ),
      );
    }
    return res.json();
  },

  converterLegacyRuleBatches: (projectId?: string) =>
    request<{ batches: LegacyRuleBatch[]; total: number }>(
      `/data-converter/legacy-rules/batches${projectId ? `?project_id=${encodeURIComponent(projectId)}` : ""}`,
    ),

  converterLegacyRuleBatch: (batchId: string) =>
    request<LegacyRuleBatch>(`/data-converter/legacy-rules/batches/${batchId}`),

  converterReviewLegacyRuleItem: (
    itemId: string,
    decision: "approved" | "rejected" | "edited" | "pending",
    opts: {
      finalTransformation?: string;
      notes?: string;
      matchedTargetTable?: string;
      matchedTargetField?: string;
    } = {},
  ) =>
    request<LegacyRuleItem>(
      `/data-converter/legacy-rules/items/${itemId}/review`,
      {
        method: "POST",
        body: JSON.stringify({
          decision,
          final_transformation: opts.finalTransformation ?? null,
          notes: opts.notes ?? "",
          matched_target_table: opts.matchedTargetTable ?? null,
          matched_target_field: opts.matchedTargetField ?? null,
        }),
      },
    ),

  converterApplyLegacyRuleBatch: (
    batchId: string,
    mappings: TableObjectMapping[],
  ) =>
    request<{ mappings: TableObjectMapping[]; rules_applied: number }>(
      `/data-converter/legacy-rules/batches/${batchId}/apply`,
      {
        method: "POST",
        body: JSON.stringify({ mappings }),
      },
    ),

  // Imports a previously generated (or externally authored/edited) STTM
  // Excel workbook and reconstructs its table/field mappings, so a
  // migration can resume from that STTM instead of regenerating one.
  converterImportSttm: async (
    file: File,
    sourceSchema?: SchemaExtractResult | null,
  ): Promise<{
    mappings: TableObjectMapping[];
    table_count: number;
    total_mapping_rows: number;
    filename: string;
  }> => {
    const formData = new FormData();
    formData.append("file", file);
    // Only the table names are needed to scope the workbook's Join Definition
    // tables. The full schema can exceed the server's 1MB form-field cap.
    if (sourceSchema) {
      formData.append(
        "known_tables_json",
        JSON.stringify(sourceSchema.tables.map((t) => t.table_name)),
      );
    }
    const res = await fetch(`${BASE}/data-converter/sttm/import`, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        formatApiErrorDetail(body.detail, `Import STTM failed: ${res.status}`),
      );
    }
    return res.json();
  },

  converterApplyRules: async (
    mappings: TableObjectMapping[],
    projectName = "Data Migration",
    opts: {
      file?: File | null;
      customRules?: CustomRule[];
      sttmFile?: File | null;
      sourceSchema?: SchemaExtractResult | null;
      projectId?: string;
    } = {},
  ): Promise<RulesApplyResponse> => {
    const formData = new FormData();
    if (opts.projectId) formData.append("project_id", opts.projectId);
    if (opts.file) formData.append("file", opts.file);
    appendJsonFile(formData, "mappings_file", mappings);
    formData.append("project_name", projectName);
    if (opts.customRules && opts.customRules.length > 0) {
      formData.append("custom_rules_json", JSON.stringify(opts.customRules));
    }
    if (opts.sttmFile) formData.append("sttm_file", opts.sttmFile);
    if (opts.sourceSchema) {
      formData.append(
        "known_tables_json",
        JSON.stringify(opts.sourceSchema.tables.map((t) => t.table_name)),
      );
    }
    const res = await fetch(`${BASE}/data-converter/rules/apply`, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        formatApiErrorDetail(body.detail, `Apply rules failed: ${res.status}`),
      );
    }
    return res.json();
  },

  converterEmailSttm: (
    fileId: string,
    recipients: string[],
    subject?: string,
    message?: string,
  ) =>
    request<{ status: string; recipients: string[]; filename: string }>(
      "/data-converter/sttm/email",
      {
        method: "POST",
        body: JSON.stringify({
          file_id: fileId,
          recipients,
          subject: subject ?? "Source-to-Target Mapping (STTM)",
          message: message ?? "Please find the attached STTM workbook.",
        }),
      },
    ),

  converterTargetConnect: (params: {
    connection?: DbConnectionConfig;
    connectionId?: string;
    projectId?: string;
    connectionName?: string;
  }) =>
    request<{
      connected: boolean;
      db_version?: string;
      schemas?: string[];
      connection_id?: string;
      // The dialect actually resolved server-side, whether from a fresh
      // form submission or an activated saved connection — needed to
      // label generated DDL comments correctly (e.g. "Snowflake Object")
      // and to derive the staging-table suffix from the actual connected
      // platform instead of assuming Salesforce/hardcoding "_sf".
      db_type?: string;
    }>("/data-converter/target/connect", {
      method: "POST",
      body: JSON.stringify({
        connection: params.connection,
        connection_id: params.connectionId,
        project_id: params.projectId,
        connection_name: params.connectionName,
      }),
    }),

  converterGenerateDdl: (
    mappings: TableObjectMapping[],
    targetSchema = "MIGRATION",
    sourceSchema?: SchemaExtractResult,
    duplicateResolutions?: DdlDuplicateResolution[],
    projectId?: string,
    targetPlatformLabel?: string,
    targetPlatform?: string | null,
    tableNaming?: {
      mode?: "auto" | "prefix" | "suffix" | "none";
      value?: string;
      scope?: string[];
    },
    includeAuditColumns = true,
  ) =>
    request<DdlGenerateResponse>("/data-converter/ddl/generate", {
      method: "POST",
      body: JSON.stringify({
        mappings,
        target_schema: targetSchema,
        ...(sourceSchema ? { source_schema: sourceSchema } : {}),
        ...(duplicateResolutions && duplicateResolutions.length > 0
          ? { duplicate_resolutions: duplicateResolutions }
          : {}),
        project_id: projectId ?? null,
        ...(targetPlatformLabel
          ? { target_platform_label: targetPlatformLabel }
          : {}),
        ...(targetPlatform ? { target_platform: targetPlatform } : {}),
        ...(tableNaming?.mode ? { table_naming_mode: tableNaming.mode } : {}),
        ...(tableNaming?.value !== undefined
          ? { table_naming_value: tableNaming.value }
          : {}),
        ...(tableNaming?.scope && tableNaming.scope.length > 0
          ? { table_naming_scope: tableNaming.scope }
          : {}),
        include_audit_columns: includeAuditColumns,
      }),
    }),

  converterExecuteDdl: (
    connectionRef: ConverterConnectionRef,
    ddlStatements: string[],
    dropIfExists = false,
    projectId?: string,
  ) =>
    request<DdlExecuteResponse>("/data-converter/ddl/execute", {
      method: "POST",
      body: JSON.stringify({
        ...converterConnRefFields("", connectionRef),
        ddl_statements: ddlStatements,
        drop_if_exists: dropIfExists,
        project_id: projectId ?? null,
      }),
    }),

  // Ahead of Create Tables — checks each mapping's target table against the
  // live database and reports per table whether it needs to be created,
  // recreated (schema drift since the mapping last matched it), or is
  // already up to date (safe to skip DDL entirely and just truncate-and-load).
  converterReconcileDdl: (
    mappings: TableObjectMapping[],
    connectionRef: ConverterConnectionRef,
    targetSchema: string,
    targetPlatform?: string | null,
    projectId?: string,
    tableNaming?: {
      mode?: "auto" | "prefix" | "suffix" | "none";
      value?: string;
      scope?: string[];
    },
  ) =>
    request<DdlReconcileResponse>("/data-converter/ddl/reconcile", {
      method: "POST",
      body: JSON.stringify({
        mappings,
        ...converterConnRefFields("", connectionRef),
        target_schema: targetSchema,
        ...(targetPlatform ? { target_platform: targetPlatform } : {}),
        project_id: projectId ?? null,
        ...(tableNaming?.mode ? { table_naming_mode: tableNaming.mode } : {}),
        ...(tableNaming?.value !== undefined
          ? { table_naming_value: tableNaming.value }
          : {}),
        ...(tableNaming?.scope && tableNaming.scope.length > 0
          ? { table_naming_scope: tableNaming.scope }
          : {}),
      }),
    }),

  // Drops target-DB tables independently of Create Tables — a standalone
  // destructive action with its own success/failure report.
  converterDropTables: (
    connectionRef: ConverterConnectionRef,
    targetSchema: string,
    tableNames: string[],
  ) =>
    request<DdlDropResponse>("/data-converter/ddl/drop", {
      method: "POST",
      body: JSON.stringify({
        ...converterConnRefFields("", connectionRef),
        target_schema: targetSchema,
        table_names: tableNames,
      }),
    }),

  // Read-only SELECT/INSERT SQL preview for one Load Data mapping — the
  // only place the Loader's SQL is ever shown to a user (no freeform SQL
  // is ever accepted/executed anywhere in this module; see
  // service.build_loader_sql_preview's docstring for why).
  converterPreviewLoaderSql: (payload: {
    sourceConnectionRef: ConverterConnectionRef;
    mapping: TableObjectMapping;
    targetSchema: string;
    pair?: Record<string, string>;
    projectId?: string;
    targetPlatform?: string | null;
  }) =>
    request<LoaderSqlPreviewOut>("/data-converter/load/sql-preview", {
      method: "POST",
      body: JSON.stringify({
        ...converterConnRefFields("source", payload.sourceConnectionRef),
        mapping: payload.mapping,
        target_schema: payload.targetSchema,
        ...(payload.pair ? { pair: payload.pair } : {}),
        ...(payload.targetPlatform !== undefined
          ? { target_platform: payload.targetPlatform }
          : {}),
        project_id: payload.projectId ?? null,
      }),
    }),

  // Every completed Oracle golden-record push for this project's MDM runs —
  // powers Data Converter's MDM Lookup "Auto-discover from MDM Agent Run"
  // mode (see mdm_routes.py's GET /oracle-outputs). Scoped to projectId —
  // mdm:read is tenant-level, so the backend relies on this filter to avoid
  // leaking another project's Oracle push targets.
  listMdmOracleOutputs: (projectId: string) =>
    request<APIEnvelopeV2<{ outputs: MdmOracleOutput[] }>>(
      `/mdm/oracle-outputs?project_id=${encodeURIComponent(projectId)}`,
    ).then((env) => env.data.outputs),

  // Starts the load as a cancellable background job; poll
  // converterLoadDataStatus for progress and converterLoadDataCancel to
  // request cancellation.
  converterLoadData: (
    sourceConnectionRef: ConverterConnectionRef,
    targetConnectionRef: ConverterConnectionRef,
    mappings: TableObjectMapping[],
    targetSchema = "MIGRATION",
    loadPairs?: {
      srcSchema: string;
      srcTable: string;
      tgtSchema: string;
      tgtTable: string;
      // MDM Lookup — left-join a second ("lookup") table on a shared match
      // field. `lookupFields` (extra, no source equivalent) are null on an
      // unmatched row; `overrideFields` name already-mapped target fields
      // whose value should come from the lookup/golden record when a match
      // exists (COALESCE), falling back to the source's own value
      // otherwise. The target table must already have columns for both;
      // this doesn't alter target DDL.
      mdmLookup?: {
        lookupSchema: string;
        lookupTable: string;
        sourceMatchField: string;
        lookupMatchField: string;
        lookupFields: string; // comma-separated column names
        overrideFields: string; // comma-separated column names
        // "mdmRun" when this lookup was auto-discovered from a real MDM
        // agent run's completed Oracle golden-record push (mdmRunId is that
        // run's id) rather than manually typed in -- that table is always
        // shaped `(cluster_id, golden_json CLOB)`, so lookupMatchField/
        // lookupFields/overrideFields name JSON keys inside golden_json
        // rather than real columns. Undefined/"manual" (the original,
        // default behavior) is unaffected.
        sourceMode?: "manual" | "mdmRun";
        mdmRunId?: string;
        // When the lookup/golden-record table lives on a DIFFERENT database
        // connection than the source (own host/credentials, even a
        // different db_type), this carries that connection — sent to the
        // backend as a JSON string (mdmLookupConnectionJson) since a real
        // cross-engine SQL JOIN isn't possible; the backend reads the
        // lookup table separately (with a row-count safety cap) and merges
        // it against the source in application code instead. Omitted (the
        // common case) means the lookup table is on the SAME connection as
        // the source, in one SQL LEFT JOIN.
        connection?: DbConnectionConfig;
      };
    }[],
    rowLimit?: number,
    // Per-table row-limit override, aligned by index with `mappings` — an
    // entry of null/undefined falls back to `rowLimit` (or unlimited) for
    // that table.
    rowLimits?: (number | null)[],
    // Load order: an ordered list of "stages", each a list of indices into
    // `mappings`. Stages run one after another; tables within a stage load
    // concurrently. Omit for fully sequential (one table per stage, in
    // mapping order) — the original behavior.
    loadOrder?: number[][],
    // Truncate the target table before loading into it (default false =
    // append). Global default for every table.
    truncateBeforeLoad?: boolean,
    // Per-table override of truncateBeforeLoad, aligned by index with
    // `mappings` — an entry of null/undefined falls back to
    // truncateBeforeLoad for that table.
    truncateTables?: (boolean | null)[],
    // If true, validates the load against the real target tables (genuine
    // type/length/null/FK constraint errors surface exactly as they would
    // in a real load) but never commits or truncates — zero permanent
    // writes. Default false (a real load).
    dryRun?: boolean,
    projectId?: string,
    // Target platform identifier (CRM target_type or RDBMS db_type) — used
    // by the backend as the staging-table suffix fallback for any
    // load_pairs entry that omits an explicit tgtTable override.
    targetPlatform?: string | null,
    // If true, appends CASCADE to the TRUNCATE TABLE statement so
    // FK-dependent child rows are cleared along with it instead of the
    // truncate failing outright. Only honored for target dialects whose
    // TRUNCATE syntax supports CASCADE (Oracle, PostgreSQL) — silently
    // ignored elsewhere. Has no effect unless truncation is happening.
    truncateCascade?: boolean,
  ) =>
    request<DataLoadStartResponse>("/data-converter/data/load", {
      method: "POST",
      body: JSON.stringify({
        ...converterConnRefFields("source", sourceConnectionRef),
        ...converterConnRefFields("target", targetConnectionRef),
        mappings,
        target_schema: targetSchema,
        ...(loadPairs
          ? {
              // Backend's load_pairs schema is `dict[str, str]` — pick only
              // the fields it understands. Callers pass richer objects
              // (e.g. loadMappingConfig, which also carries client-only
              // bookkeeping like `included`/`rowLimit`/`loadStage`) that
              // must not leak through as extra, wrongly-typed keys.
              load_pairs: loadPairs.map((pair) => ({
                srcSchema: pair.srcSchema,
                srcTable: pair.srcTable,
                tgtSchema: pair.tgtSchema,
                tgtTable: pair.tgtTable,
                ...(pair.mdmLookup
                  ? {
                      mdmLookupSchema: pair.mdmLookup.lookupSchema,
                      mdmLookupTable: pair.mdmLookup.lookupTable,
                      mdmSourceMatchField: pair.mdmLookup.sourceMatchField,
                      mdmLookupMatchField: pair.mdmLookup.lookupMatchField,
                      mdmLookupFields: pair.mdmLookup.lookupFields,
                      mdmOverrideFields: pair.mdmLookup.overrideFields,
                      ...(pair.mdmLookup.sourceMode === "mdmRun"
                        ? { mdmLookupIsGoldenJson: "true" }
                        : {}),
                      ...(pair.mdmLookup.connection
                        ? {
                            mdmLookupConnectionJson: JSON.stringify(
                              pair.mdmLookup.connection,
                            ),
                          }
                        : {}),
                    }
                  : {}),
              })),
            }
          : {}),
        ...(rowLimit !== undefined ? { row_limit: rowLimit } : {}),
        ...(rowLimits !== undefined ? { row_limits: rowLimits } : {}),
        ...(loadOrder !== undefined ? { load_order: loadOrder } : {}),
        ...(truncateBeforeLoad !== undefined
          ? { truncate_before_load: truncateBeforeLoad }
          : {}),
        ...(truncateTables !== undefined
          ? { truncate_tables: truncateTables }
          : {}),
        ...(truncateCascade !== undefined
          ? { truncate_cascade: truncateCascade }
          : {}),
        ...(dryRun !== undefined ? { dry_run: dryRun } : {}),
        // Only set when explicitly passed — this must NOT clobber the
        // `project_id` converterConnRefFields already put in the body above
        // from each connectionRef's own `projectId` (e.g. a caller that
        // forgets this trailing arg would otherwise silently null out an
        // already-correct project_id and hit the backend's "Either
        // 'connection' or both 'connection_id' and 'project_id' are
        // required" error even though a valid connection_id was sent).
        ...(projectId !== undefined ? { project_id: projectId } : {}),
        ...(targetPlatform !== undefined
          ? { target_platform: targetPlatform }
          : {}),
      }),
    }),

  converterLoadDataStatus: (jobId: string) =>
    request<DataLoadStatusResponse>(
      `/data-converter/data/load-status/${jobId}`,
    ),

  converterLoadDataCancel: (jobId: string) =>
    request<DataLoadStatusResponse>(
      `/data-converter/data/load-cancel/${jobId}`,
      { method: "POST" },
    ),

  // ── DB → Salesforce Load ─────────────────────────────────────────────────
  converterLoadDbToSf: (payload: {
    sourceConnectionRef: ConverterConnectionRef;
    sfConnection: SalesforceConnectionConfig;
    sourceSchema: string;
    sourceTable: string;
    sfObject: string;
    fieldMappings: {
      source_column: string;
      sf_field: string;
      // dict: literal FROM=TO exact-match lookup. string: a Transform-engine
      // expression or plain-English instruction (e.g. "UPPER(FirstName)",
      // "M as Male, F as Female") evaluated per row.
      value_map?: Record<string, string> | string;
      sf_type?: string;
    }[];
    rowLimit?: number;
    batchSize?: number;
    operation?: "insert" | "upsert" | "update";
    upsertKey?: string;
    projectId?: string;
  }) =>
    request<DbToSfLoadStartResponse>("/data-converter/sf/load-from-db", {
      method: "POST",
      body: JSON.stringify({
        ...converterConnRefFields("source", payload.sourceConnectionRef),
        sf_connection: payload.sfConnection,
        source_schema: payload.sourceSchema,
        source_table: payload.sourceTable,
        sf_object: payload.sfObject,
        field_mappings: payload.fieldMappings,
        ...(payload.rowLimit !== undefined
          ? { row_limit: payload.rowLimit }
          : {}),
        ...(payload.batchSize !== undefined
          ? { batch_size: payload.batchSize }
          : {}),
        ...(payload.operation ? { operation: payload.operation } : {}),
        ...(payload.upsertKey ? { upsert_key: payload.upsertKey } : {}),
        project_id: payload.projectId ?? null,
      }),
    }),

  converterLoadDbToSfStatus: (jobId: string) =>
    request<DbToSfLoadStatusResponse>(
      `/data-converter/sf/load-status/${jobId}`,
    ),

  converterLoadDbToSfCancel: (jobId: string) =>
    request<DbToSfLoadStatusResponse>(
      `/data-converter/sf/load-cancel/${jobId}`,
      { method: "POST" },
    ),

  // ── CRM/SaaS → DB streaming load ─────────────────────────────────────────
  // Streams a single CRM/SaaS object's records directly into a target DB
  // table, batch-by-batch — unlike converterParseLegacyCrm (extract-as-CSV,
  // then file-mode Load Data), this never materializes the full object in
  // memory. See issue #735.
  converterLoadCrmToDb: (payload: {
    connection: SalesforceConnectionConfig;
    objectName: string;
    targetConnectionRef: ConverterConnectionRef;
    targetSchema: string;
    targetTable: string;
    fieldMappings: CrmToDbFieldMapping[];
    batchSize?: number;
    truncate?: boolean;
    projectId?: string;
  }) =>
    request<CrmToDbLoadStartResponse>("/data-converter/crm/load-from-crm", {
      method: "POST",
      body: JSON.stringify({
        connection: payload.connection,
        object_name: payload.objectName,
        ...converterConnRefFields("target", payload.targetConnectionRef),
        // Set after the spread, like every other call site in this file --
        // otherwise a saved connection ref's own project_id would silently
        // override this explicit one if they ever differ.
        project_id: payload.projectId ?? null,
        target_schema: payload.targetSchema,
        target_table: payload.targetTable,
        field_mappings: payload.fieldMappings,
        ...(payload.batchSize !== undefined
          ? { batch_size: payload.batchSize }
          : {}),
        ...(payload.truncate !== undefined
          ? { truncate: payload.truncate }
          : {}),
      }),
    }),

  converterLoadCrmToDbStatus: (jobId: string) =>
    request<CrmToDbLoadStatusResponse>(
      `/data-converter/crm/load-status/${jobId}`,
    ),

  converterLoadCrmToDbCancel: (jobId: string) =>
    request<CrmToDbLoadStatusResponse>(
      `/data-converter/crm/load-cancel/${jobId}`,
      { method: "POST" },
    ),

  // ── Reconciliation Dashboard ─────────────────────────────────────────────
  converterReconcileRowLimits: () =>
    request<ReconcileRowLimitsResponse>("/data-converter/reconcile/row-limits"),

  converterReconcileCounts: (payload: {
    sourceConnectionRef: ConverterConnectionRef;
    targetConnectionRef: ConverterConnectionRef;
    mappings: TableObjectMapping[];
    targetSchema: string;
    pairs?: {
      srcSchema: string;
      srcTable: string;
      tgtSchema: string;
      tgtTable: string;
      // Carried over from the Load Data step's MDM Lookup config (if any) so
      // reconciliation can compare target count against the lookup table's
      // own count, rather than only the raw (pre-filter) source count.
      mdmLookupSchema?: string;
      mdmLookupTable?: string;
    }[];
    projectId?: string;
    targetPlatform?: string | null;
  }) =>
    request<ReconcileCountResponse>("/data-converter/reconcile/counts", {
      method: "POST",
      body: JSON.stringify({
        ...converterConnRefFields("source", payload.sourceConnectionRef),
        ...converterConnRefFields("target", payload.targetConnectionRef),
        mappings: payload.mappings,
        target_schema: payload.targetSchema,
        ...(payload.pairs ? { pairs: payload.pairs } : {}),
        ...(payload.targetPlatform !== undefined
          ? { target_platform: payload.targetPlatform }
          : {}),
        project_id: payload.projectId ?? null,
      }),
    }),

  converterReconcileColumns: (payload: {
    sourceConnectionRef: ConverterConnectionRef;
    targetConnectionRef: ConverterConnectionRef;
    mapping: TableObjectMapping;
    targetSchema: string;
    pair?: {
      srcSchema: string;
      srcTable: string;
      tgtSchema: string;
      tgtTable: string;
    };
    projectId?: string;
    targetPlatform?: string | null;
  }) =>
    request<ReconcileColumnResponse>("/data-converter/reconcile/columns", {
      method: "POST",
      body: JSON.stringify({
        ...converterConnRefFields("source", payload.sourceConnectionRef),
        ...converterConnRefFields("target", payload.targetConnectionRef),
        mapping: payload.mapping,
        target_schema: payload.targetSchema,
        ...(payload.pair ? { pair: payload.pair } : {}),
        ...(payload.targetPlatform !== undefined
          ? { target_platform: payload.targetPlatform }
          : {}),
        project_id: payload.projectId ?? null,
      }),
    }),

  converterReconcileRows: (payload: {
    sourceConnectionRef: ConverterConnectionRef;
    targetConnectionRef: ConverterConnectionRef;
    mapping: TableObjectMapping;
    targetSchema: string;
    pair?: {
      srcSchema: string;
      srcTable: string;
      tgtSchema: string;
      tgtTable: string;
    };
    keyColumns: string[];
    projectId?: string;
    targetPlatform?: string | null;
  }) =>
    request<ReconcileRowResponse>("/data-converter/reconcile/rows", {
      method: "POST",
      body: JSON.stringify({
        ...converterConnRefFields("source", payload.sourceConnectionRef),
        ...converterConnRefFields("target", payload.targetConnectionRef),
        mapping: payload.mapping,
        target_schema: payload.targetSchema,
        ...(payload.pair ? { pair: payload.pair } : {}),
        key_columns: payload.keyColumns,
        ...(payload.targetPlatform !== undefined
          ? { target_platform: payload.targetPlatform }
          : {}),
        project_id: payload.projectId ?? null,
      }),
    }),

  // File-source counterparts — Files-mode load, no source DB connection to
  // reconcile against; source_files/source_file carry each mapped table's
  // own uploaded file (path/format from the Loader step's per-row config).
  converterReconcileFileCounts: (payload: {
    sourceFiles: Record<string, FileReconcileSourceFile>;
    targetConnectionRef: ConverterConnectionRef;
    mappings: TableObjectMapping[];
    targetSchema: string;
    pairs?: {
      srcSchema: string;
      srcTable: string;
      tgtSchema: string;
      tgtTable: string;
    }[];
    projectId?: string;
  }) =>
    request<ReconcileCountResponse>("/data-converter/reconcile/file-counts", {
      method: "POST",
      body: JSON.stringify({
        source_files: payload.sourceFiles,
        ...converterConnRefFields("target", payload.targetConnectionRef),
        mappings: payload.mappings,
        target_schema: payload.targetSchema,
        ...(payload.pairs ? { pairs: payload.pairs } : {}),
        project_id: payload.projectId ?? null,
      }),
    }),

  converterReconcileFileColumns: (payload: {
    sourceFile: FileReconcileSourceFile;
    targetConnectionRef: ConverterConnectionRef;
    mapping: TableObjectMapping;
    targetSchema: string;
    pair?: {
      srcSchema: string;
      srcTable: string;
      tgtSchema: string;
      tgtTable: string;
    };
    projectId?: string;
    targetPlatform?: string | null;
  }) =>
    request<ReconcileColumnResponse>("/data-converter/reconcile/file-columns", {
      method: "POST",
      body: JSON.stringify({
        source_file: payload.sourceFile,
        ...converterConnRefFields("target", payload.targetConnectionRef),
        mapping: payload.mapping,
        target_schema: payload.targetSchema,
        ...(payload.pair ? { pair: payload.pair } : {}),
        ...(payload.targetPlatform !== undefined
          ? { target_platform: payload.targetPlatform }
          : {}),
        project_id: payload.projectId ?? null,
      }),
    }),

  converterReconcileFileRows: (payload: {
    sourceFile: FileReconcileSourceFile;
    targetConnectionRef: ConverterConnectionRef;
    mapping: TableObjectMapping;
    targetSchema: string;
    pair?: {
      srcSchema: string;
      srcTable: string;
      tgtSchema: string;
      tgtTable: string;
    };
    keyColumns: string[];
    projectId?: string;
    targetPlatform?: string | null;
  }) =>
    request<ReconcileRowResponse>("/data-converter/reconcile/file-rows", {
      method: "POST",
      body: JSON.stringify({
        source_file: payload.sourceFile,
        ...converterConnRefFields("target", payload.targetConnectionRef),
        mapping: payload.mapping,
        target_schema: payload.targetSchema,
        ...(payload.pair ? { pair: payload.pair } : {}),
        key_columns: payload.keyColumns,
        ...(payload.targetPlatform !== undefined
          ? { target_platform: payload.targetPlatform }
          : {}),
        project_id: payload.projectId ?? null,
      }),
    }),

  // ── Data Converter — project-scoped persistence (standalone actions) ────
  // Lets "Data Loader"/"Reconciliation"/"Load to Target" run independently,
  // in a fresh session, by pulling the mapping a prior "Source -> Target
  // Mapping" run produced instead of requiring it still be in memory.
  converterGetProjectMapping: (projectId: string) =>
    request<ConverterProjectMappingOut | null>(
      `/data-converter/project/${projectId}/mapping`,
    ),

  converterSetProjectMapping: (
    projectId: string,
    mappings: TableObjectMapping[],
  ) =>
    request<ConverterProjectMappingOut>(
      `/data-converter/project/${projectId}/mapping`,
      { method: "PUT", body: JSON.stringify({ mappings }) },
    ),

  converterDeleteProjectMapping: (projectId: string) =>
    request<void>(`/data-converter/project/${projectId}/mapping`, {
      method: "DELETE",
    }),

  // Mapping save history — up to the 5 most recent saved-mapping entries
  // (newest first), for the mapping action's "pick which version to resume
  // from" list. List is lightweight (id/timestamp/table count only); fetch
  // the full mappings for one entry only once it's actually selected.
  converterListMappingHistory: (projectId: string) =>
    request<ConverterMappingHistoryEntryOut[]>(
      `/data-converter/project/${projectId}/mapping-history`,
    ),

  converterGetMappingHistoryEntry: (projectId: string, historyId: string) =>
    request<ConverterMappingHistoryDetailOut>(
      `/data-converter/project/${projectId}/mapping-history/${historyId}`,
    ),

  // Schema scan snapshot — read-only, written as a side effect of
  // /source/scan and /target/scan; exposes it back to the client so a scan
  // result is resumable too, not just diffable against the next scan.
  converterGetSchemaSnapshot: (projectId: string, kind: "source" | "target") =>
    request<ConverterSchemaSnapshotOut | null>(
      `/data-converter/project/${projectId}/schema-snapshot/${kind}`,
    ),

  converterGetDdlState: (projectId: string) =>
    request<ConverterDdlStateOut | null>(
      `/data-converter/project/${projectId}/ddl-state`,
    ),

  converterSetDdlState: (
    projectId: string,
    state: {
      target_schema: string;
      ddl_statements: DdlStatement[];
      duplicate_report?: DdlDuplicate[];
      executed?: boolean;
      target_platform_mode?: "crm" | "rdbms" | "mirror" | null;
    },
  ) =>
    request<ConverterDdlStateOut>(
      `/data-converter/project/${projectId}/ddl-state`,
      { method: "PUT", body: JSON.stringify(state) },
    ),

  converterDeleteDdlState: (projectId: string) =>
    request<void>(`/data-converter/project/${projectId}/ddl-state`, {
      method: "DELETE",
    }),

  converterGetReconciliation: (projectId: string) =>
    request<ConverterReconciliationStateOut | null>(
      `/data-converter/project/${projectId}/reconciliation`,
    ),

  converterSetReconciliation: (
    projectId: string,
    state: {
      counts_result?: Record<string, unknown> | null;
      columns_result?: Record<string, unknown> | null;
      quality_result?: Record<string, unknown> | null;
    },
  ) =>
    request<ConverterReconciliationStateOut>(
      `/data-converter/project/${projectId}/reconciliation`,
      { method: "PUT", body: JSON.stringify(state) },
    ),

  converterDeleteReconciliation: (projectId: string) =>
    request<void>(`/data-converter/project/${projectId}/reconciliation`, {
      method: "DELETE",
    }),

  // Issue #599 — incremental (CDC-lite) Data Loader watermark.
  converterGetLoadWatermark: (projectId: string, targetTable: string) =>
    request<ConverterLoadWatermarkOut | null>(
      `/data-converter/project/${projectId}/incremental-watermark/${encodeURIComponent(targetTable)}`,
    ),

  // "Force full reload": deletes the saved watermark so the next
  // incremental run re-reads every row from scratch.
  converterResetLoadWatermark: (projectId: string, targetTable: string) =>
    request<void>(
      `/data-converter/project/${projectId}/incremental-watermark/${encodeURIComponent(targetTable)}`,
      { method: "DELETE" },
    ),

  // Detailed reconciliation — row counts + full column detail + a derived
  // per-table/overall data-quality score, in one call.
  converterReconcileDetailed: (payload: {
    sourceConnectionRef: ConverterConnectionRef;
    targetConnectionRef: ConverterConnectionRef;
    mappings: TableObjectMapping[];
    targetSchema: string;
    pairs?: {
      srcSchema: string;
      srcTable: string;
      tgtSchema: string;
      tgtTable: string;
      mdmLookupSchema?: string;
      mdmLookupTable?: string;
    }[];
    projectId?: string;
    targetPlatform?: string | null;
  }) =>
    request<ConverterReconcileDetailedOut>(
      "/data-converter/reconcile/detailed",
      {
        method: "POST",
        body: JSON.stringify({
          ...converterConnRefFields("source", payload.sourceConnectionRef),
          ...converterConnRefFields("target", payload.targetConnectionRef),
          mappings: payload.mappings,
          target_schema: payload.targetSchema,
          ...(payload.pairs ? { pairs: payload.pairs } : {}),
          ...(payload.targetPlatform !== undefined
            ? { target_platform: payload.targetPlatform }
            : {}),
          project_id: payload.projectId ?? null,
        }),
      },
    ),

  // Data Loader run history — up to the 10 most recent runs (newest first).
  converterListLoadHistory: (projectId: string) =>
    request<ConverterLoadHistoryEntryOut[]>(
      `/data-converter/project/${projectId}/load-history`,
    ),

  converterGetLoadHistoryEntry: (projectId: string, historyId: string) =>
    request<ConverterLoadHistoryDetailOut>(
      `/data-converter/project/${projectId}/load-history/${historyId}`,
    ),

  converterSaveLoadHistory: (
    projectId: string,
    entry: {
      job_type: "db_to_db" | "db_to_sf";
      status: "completed" | "failed" | "cancelled";
      summary: Record<string, unknown>;
    },
  ) =>
    request<{ status: string }>(
      `/data-converter/project/${projectId}/load-history`,
      { method: "PUT", body: JSON.stringify(entry) },
    ),

  // Reconciliation run history — up to the 10 most recent runs (newest first).
  converterListReconciliationHistory: (projectId: string) =>
    request<ConverterReconciliationHistoryEntryOut[]>(
      `/data-converter/project/${projectId}/reconciliation-history`,
    ),

  converterGetReconciliationHistoryEntry: (
    projectId: string,
    historyId: string,
  ) =>
    request<ConverterReconciliationHistoryDetailOut>(
      `/data-converter/project/${projectId}/reconciliation-history/${historyId}`,
    ),

  converterSaveReconciliationHistory: (
    projectId: string,
    entry: {
      counts_result?: Record<string, unknown> | null;
      columns_result?: Record<string, unknown> | null;
      quality_result?: Record<string, unknown> | null;
      overall_score?: number | null;
    },
  ) =>
    request<{ status: string }>(
      `/data-converter/project/${projectId}/reconciliation-history`,
      { method: "PUT", body: JSON.stringify(entry) },
    ),

  // Load to Target run history — up to the 10 most recent runs (newest first).
  converterListFinalLoadHistory: (projectId: string) =>
    request<ConverterFinalLoadHistoryEntryOut[]>(
      `/data-converter/project/${projectId}/final-load-history`,
    ),

  converterGetFinalLoadHistoryEntry: (projectId: string, historyId: string) =>
    request<ConverterFinalLoadHistoryDetailOut>(
      `/data-converter/project/${projectId}/final-load-history/${historyId}`,
    ),

  converterSaveFinalLoadHistory: (
    projectId: string,
    entry: {
      target_type: string;
      status: "completed" | "failed" | "cancelled";
      summary: Record<string, unknown>;
    },
  ) =>
    request<{ status: string }>(
      `/data-converter/project/${projectId}/final-load-history`,
      { method: "PUT", body: JSON.stringify(entry) },
    ),

  // Final-table Data Quality report — a detailed scorecard for the
  // project's post-load target tables.
  converterGetFinalTableDq: (projectId: string) =>
    request<ConverterFinalTableDqOut | null>(
      `/data-converter/project/${projectId}/final-table-dq`,
    ),

  converterGenerateFinalTableDq: (
    projectId: string,
    payload: { tables: TableQualityResult[]; overall_score: number },
  ) =>
    request<ConverterFinalTableDqOut>(
      `/data-converter/project/${projectId}/final-table-dq`,
      { method: "PUT", body: JSON.stringify(payload) },
    ),

  converterGetLatestLoadJob: (
    projectId: string,
    jobType: "db_to_db" | "db_to_sf",
  ) =>
    request<ConverterLatestLoadJobOut | null>(
      `/data-converter/project/${projectId}/latest-load-job?job_type=${jobType}`,
    ),

  converterGetCrmConnection: (projectId: string) =>
    request<CrmConnectionOut | null>(
      `/data-converter/project/${projectId}/crm-connection`,
    ),

  converterSetCrmConnection: (
    projectId: string,
    data: Partial<SalesforceConnectionConfig>,
  ) =>
    request<CrmConnectionOut>(
      `/data-converter/project/${projectId}/crm-connection`,
      { method: "PUT", body: JSON.stringify(data) },
    ),

  converterDeleteCrmConnection: (projectId: string) =>
    request<void>(`/data-converter/project/${projectId}/crm-connection`, {
      method: "DELETE",
    }),

  // ── Data Converter — functional-domain skills ────────────────────────────
  // Selectable per project (Child Support, Child Welfare, Integrated
  // Eligibility, Medicaid, ...) — biases the mapping generator's
  // suggestions toward that domain's vocabulary.
  converterListSkills: () =>
    request<ConverterSkillInfo[]>("/data-converter/skills"),

  // Orthogonal to the functional-domain skills above — toggles cross-agent
  // enrichment (Metadata Curator / Data Profiling context) and whether the
  // STTM Finalize action also generates a Data Dictionary document.
  // Selections are stored in the same project-skills list as functional
  // skills (see converterGetProjectSkills/converterSetProjectSkills).
  converterListIntegrationSkills: () =>
    request<ConverterSkillInfo[]>("/data-converter/integration-skills"),

  converterGetProjectSkills: (projectId: string) =>
    request<ConverterProjectSkillsOut>(
      `/data-converter/project/${projectId}/skills`,
    ),

  converterSetProjectSkills: (projectId: string, skills: string[]) =>
    request<ConverterProjectSkillsOut>(
      `/data-converter/project/${projectId}/skills`,
      { method: "PUT", body: JSON.stringify({ skills }) },
    ),

  // ── Data Converter — mapping-step knowledge base ─────────────────────────
  // Project Knowledge Base — reference documents (source/target data
  // dictionaries, data models, functional specs, or other supporting
  // material) attached to a project, shared across agents. Content is
  // used deterministically (plain-text search) — never sent to an LLM.
  projectUploadKnowledgeDoc: async (
    projectId: string,
    file: File,
    docType: ProjectKnowledgeDocType,
  ): Promise<ProjectKnowledgeDocInfo> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docType);
    const res = await fetch(`${BASE}/projects/${projectId}/knowledge`, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        body.detail || `Knowledge base document upload failed: ${res.status}`,
      );
    }
    return res.json();
  },

  projectListKnowledgeDocs: (projectId: string) =>
    request<ProjectKnowledgeDocInfo[]>(`/projects/${projectId}/knowledge`),

  projectDeleteKnowledgeDoc: (projectId: string, docId: string) =>
    request<void>(`/projects/${projectId}/knowledge/${docId}`, {
      method: "DELETE",
    }),

  // ── Data Converter — upload a data-mapping document ──────────────────────
  // At the user's discretion: "replace" adopts the parsed document as the
  // project's mapping outright; "seed" treats it as an authoritative
  // starting point and runs the normal heuristic/LLM generator to fill any
  // gaps the document didn't cover (requires the current source schema +
  // target objects so the generator has something to fill gaps against).
  converterImportMappingDocument: async (
    file: File,
    mode: "replace" | "seed",
    opts: {
      projectId?: string;
      sourceSchema?: SchemaExtractResult;
      sfObjects?: SalesforceObjectMeta[];
      llmEnhance?: boolean;
    } = {},
  ): Promise<ConverterMappingImportResult> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("mode", mode);
    if (opts.projectId) formData.append("project_id", opts.projectId);
    // Only "seed" mode reads the schema/target objects ("replace" ignores them).
    if (mode === "seed" && opts.sourceSchema) {
      appendJsonFile(formData, "source_schema_file", opts.sourceSchema);
    }
    if (mode === "seed" && opts.sfObjects) {
      appendJsonFile(formData, "sf_objects_file", opts.sfObjects);
    }
    if (opts.llmEnhance !== undefined) {
      formData.append("llm_enhance", String(opts.llmEnhance));
    }
    const res = await fetch(`${BASE}/data-converter/mapping/import-document`, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        body.detail || `Mapping document import failed: ${res.status}`,
      );
    }
    return res.json();
  },

  // ── Data Converter — saved connections library ──────────────────────────
  // A library of source/target DB connections per project, additive on top
  // of the existing single "active" source-db/target-db slot — lets a user
  // build up several connections over time and pick which one to activate.
  converterListSavedConnections: (
    projectId: string,
    kind: "source" | "target" | "crm",
  ) =>
    request<SavedConnectionOut[]>(
      `/data-converter/project/${projectId}/saved-connections?kind=${encodeURIComponent(kind)}`,
    ),

  converterCreateSavedConnection: (
    projectId: string,
    data: (
      | Partial<DbConnectionConfig>
      | Partial<SalesforceConnectionConfig>
    ) & {
      kind: "source" | "target" | "crm";
      label?: string;
    },
  ) =>
    request<SavedConnectionOut>(
      `/data-converter/project/${projectId}/saved-connections`,
      { method: "POST", body: JSON.stringify(data) },
    ),

  converterRenameSavedConnection: (
    projectId: string,
    connectionId: string,
    label: string,
  ) =>
    request<SavedConnectionOut>(
      `/data-converter/project/${projectId}/saved-connections/${connectionId}`,
      { method: "PUT", body: JSON.stringify({ label }) },
    ),

  // Fixes a saved connection's stored credential — the recovery path when
  // "this saved connection's stored password could not be found" (e.g. an
  // ephemeral dev Vault got reset). Omit a field to leave it unchanged;
  // providing password/clientSecret also re-activates the connection
  // server-side so an already-active connection's live slot picks up the
  // fix immediately.
  converterUpdateSavedConnection: (
    projectId: string,
    connectionId: string,
    updates: {
      label?: string;
      password?: string;
      clientSecret?: string;
      securityToken?: string;
      refreshToken?: string;
    },
  ) =>
    request<SavedConnectionOut>(
      `/data-converter/project/${projectId}/saved-connections/${connectionId}`,
      {
        method: "PUT",
        body: JSON.stringify({
          ...(updates.label !== undefined ? { label: updates.label } : {}),
          ...(updates.password !== undefined
            ? { password: updates.password }
            : {}),
          ...(updates.clientSecret !== undefined
            ? { client_secret: updates.clientSecret }
            : {}),
          ...(updates.securityToken !== undefined
            ? { security_token: updates.securityToken }
            : {}),
          ...(updates.refreshToken !== undefined
            ? { refresh_token: updates.refreshToken }
            : {}),
        }),
      },
    ),

  converterDeleteSavedConnection: (projectId: string, connectionId: string) =>
    request<void>(
      `/data-converter/project/${projectId}/saved-connections/${connectionId}`,
      { method: "DELETE" },
    ),

  // asKind: reuse a "source"-kind row as the active "target" (or vice
  // versa) instead of the row's own kind — omit to activate as its own kind.
  converterActivateSavedConnection: (
    projectId: string,
    connectionId: string,
    asKind?: "source" | "target",
  ) =>
    request<SavedConnectionOut>(
      `/data-converter/project/${projectId}/saved-connections/${connectionId}/activate${
        asKind ? `?as_kind=${asKind}` : ""
      }`,
      { method: "POST" },
    ),

  /** POST /api/v2/validation/dry-run — preview which rules a pack would apply. */
  validation: {
    dryRun: (data: ValidationDryRunRequest) =>
      request<ValidationDryRunResponse>("/validation/dry-run", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },
};

// ---------------------------------------------------------------------------
// Pipelines engine — progress polling for converterLoadBatchPipeline runs.
// Mounted at /api/v1/pipeline-runs (not /api/v2 like everything else above),
// so these go through a standalone fetch rather than apiv2's `request()`
// (hardcoded to the /api/v2 BASE).
// ---------------------------------------------------------------------------

export interface PipelineStageProgress {
  stage_id: string;
  stage_name: string;
  stage_index: number;
  status: string;
  completed_tasks: number;
  total_tasks: number;
  percent: number;
}

export interface PipelineProgressResponse {
  pipeline_run_id: string;
  pipeline_percent: number;
  stage_statuses: PipelineStageProgress[];
  current_stage: string | null;
  eta_seconds: number | null;
  succeeded_count: number;
  failed_count: number;
  total_count: number;
}

export async function getPipelineProgress(
  runId: string,
): Promise<PipelineProgressResponse> {
  const res = await fetch(`/api/v1/pipeline-runs/${runId}/progress`, {
    credentials: "include",
  });
  if (res.status === 401) {
    clearAuth();
    throw new Error("Session expired — please log in again.");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(
      formatApiErrorDetail(body.detail, `Request failed: ${res.status}`),
    );
  }
  return res.json();
}

/** The server's closing frame for an open progress stream. */
export interface ClosingFrame {
  status?: number;
  /** "access_revoked": access ended or the run is gone (final).
   * "reverify_failed": the server could not re-check access (transient). */
  code?: string;
  detail?: string;
}

/** True when a closing frame means access is really gone (stop for good).
 * `reverify_failed` (status 503) is a transient server-side failure and is
 * NOT final; a frame with no code is read by its status. */
export function closingFrameIsFinal(frame: ClosingFrame): boolean {
  if (frame.code === "reverify_failed") return false;
  if (frame.code === undefined && frame.status === 503) return false;
  return true;
}

/** Opens the pipelines engine's SSE progress stream. Named events
 * ("progress" / "complete"), not the generic unnamed `message` event —
 * see ztd_app/api/modules/pipelines/router.py's stream_progress.
 *
 * The server re-checks the caller's access to the run while the stream is
 * open and ends it with one "access_revoked" event ({status, code, detail}).
 * With code "access_revoked" (403/404: access gone or the run no longer
 * exists) that is final: the stream is closed here (no reconnect) and
 * `onAccessRevoked` tells the caller to stop polling too. With code
 * "reverify_failed" (503: the server could not re-check) nothing is closed or
 * stopped; the browser reconnects and polling continues. */
export function subscribeToPipelineProgress(
  runId: string,
  onProgress: (progress: PipelineProgressResponse) => void,
  onComplete: (status: string) => void,
  onAccessRevoked?: (status: number) => void,
): EventSource {
  const es = new EventSource(`/api/v1/pipeline-runs/${runId}/progress/stream`, {
    withCredentials: true,
  });
  es.addEventListener("progress", (evt) => {
    try {
      onProgress(JSON.parse((evt as MessageEvent).data));
    } catch {
      // non-JSON frame — ignore
    }
  });
  es.addEventListener("complete", (evt) => {
    try {
      const data = JSON.parse((evt as MessageEvent).data) as {
        status: string;
      };
      onComplete(data.status);
    } catch {
      onComplete("unknown");
    }
  });
  es.addEventListener("access_revoked", (evt) => {
    let frame: ClosingFrame = { status: 403 };
    try {
      frame = JSON.parse((evt as MessageEvent).data) as ClosingFrame;
    } catch {
      // keep the default: a frame we cannot read is treated as final
    }
    if (!closingFrameIsFinal(frame)) {
      // The server could not re-check access for a few seconds (database
      // trouble) and ended this stream; the run itself is fine. Do NOT close
      // or stop polling: the browser reconnects this EventSource, and the
      // server re-authorizes on connect.
      return;
    }
    es.close();
    onAccessRevoked?.(frame.status ?? 403);
  });
  es.onerror = () => {
    // SSE auto-reconnects; the polling fallback covers any gap. (A refused
    // reconnect — 403/404 — makes the browser close the stream for good.)
  };
  return es;
}

export interface ConverterProjectMappingOut {
  project_id: string;
  mappings: TableObjectMapping[];
}

export interface ConverterMappingHistoryEntryOut {
  id: string;
  saved_at: string;
  table_count: number;
}

export interface ConverterMappingHistoryDetailOut {
  id: string;
  saved_at: string;
  mappings: TableObjectMapping[];
}

export interface ConverterSchemaSnapshotOut {
  project_id: string;
  kind: "source" | "target";
  schema_result: SchemaExtractResult;
}

export interface ConverterDdlStateOut {
  project_id: string;
  target_schema: string;
  ddl_statements: DdlStatement[];
  duplicate_report: DdlDuplicate[];
  executed: boolean;
  executed_at: string | null;
  updated_at: string;
  target_platform_mode?: "crm" | "rdbms" | "mirror" | null;
}

export interface ConverterReconciliationStateOut {
  project_id: string;
  counts_result: Record<string, unknown> | null;
  columns_result: Record<string, unknown> | null;
  quality_result: Record<string, unknown> | null;
  updated_at: string;
}

// Issue #599 — current incremental-load watermark for one target table.
export interface ConverterLoadWatermarkOut {
  project_id: string;
  target_table: string;
  incremental_column: string;
  last_value: { type: string; value: unknown };
  last_run_at: string;
}

export interface TableQualityResult {
  source_table: string;
  target_table: string;
  row_count_match: boolean;
  source_count: number | null;
  target_count: number | null;
  completeness_pct: number | null;
  consistency_pct: number | null;
  uniqueness_pct: number | null;
  validity_issues: string[];
  column_count: number;
  columns_reviewed: number;
  overall_score: number;
  error: string | null;
}

export interface ConverterReconcileDetailedOut {
  counts_result: Record<string, unknown>;
  columns_result: Record<string, unknown>;
  tables: TableQualityResult[];
  overall_score: number;
}

// ── Resume-picker history (Data Loader / Reconciliation / Load to Target) ──
// Mirrors ConverterMappingHistoryEntryOut/ConverterMappingHistoryDetailOut.

export interface ConverterLoadHistoryEntryOut {
  id: string;
  saved_at: string;
  job_type: string;
  status: string;
  total_tables: number;
  total_rows_loaded: number;
  total_rows_failed: number;
}

export interface ConverterLoadHistoryDetailOut {
  id: string;
  saved_at: string;
  job_type: string;
  status: string;
  summary: Record<string, unknown>;
}

export interface ConverterReconciliationHistoryEntryOut {
  id: string;
  saved_at: string;
  table_count: number;
  overall_score: number | null;
}

export interface ConverterReconciliationHistoryDetailOut {
  id: string;
  saved_at: string;
  counts_result: Record<string, unknown> | null;
  columns_result: Record<string, unknown> | null;
  quality_result: Record<string, unknown> | null;
  overall_score: number | null;
}

export interface ConverterFinalLoadHistoryEntryOut {
  id: string;
  saved_at: string;
  target_type: string;
  status: string;
  rows_loaded: number;
  rows_failed: number;
}

export interface ConverterFinalLoadHistoryDetailOut {
  id: string;
  saved_at: string;
  target_type: string;
  status: string;
  summary: Record<string, unknown>;
}

export interface ConverterFinalTableDqOut {
  project_id: string;
  report: {
    tables: TableQualityResult[];
    overall_score: number;
    generated_at: string;
    table_count: number;
    tables_with_issues: number;
  };
  generated_at: string;
}

export interface ConverterLatestLoadJobOut {
  job_id: string;
  job_type: "db_to_db" | "db_to_sf";
  status: string;
  created_at: string;
  // Recalled from the job's own request_meta — the staging schema/table a
  // db_to_sf job actually read from, present even when converter_ddl_states
  // has no row for the project.
  source_schema?: string | null;
  source_table?: string | null;
}

export interface ConverterSkillInfo {
  slug: string;
  label: string;
  description: string;
}

export interface ConverterProjectSkillsOut {
  project_id: string;
  skills: string[];
}

export interface ConverterMappingImportResult {
  mappings: TableObjectMapping[];
  warnings: string[];
  mode: "replace" | "seed";
}

export type ProjectKnowledgeDocType =
  | "source_data_dictionary"
  | "target_data_dictionary"
  | "source_data_model"
  | "target_data_model"
  | "source_functional_spec"
  | "target_functional_spec"
  | "other";

export interface ProjectKnowledgeDocInfo {
  id: string;
  project_id: string;
  doc_type: ProjectKnowledgeDocType;
  filename: string;
  text_length: number;
  uploaded_at: string;
}

export interface SavedConnectionOut {
  id: string;
  kind: "source" | "target" | "crm";
  label: string;
  is_active: boolean;
  db_type: string;
  host: string;
  port: number | null;
  database: string;
  schema_name: string;
  username: string;
  auth_type: string;
  account: string;
  warehouse: string;
  role: string;
  service_name: string;
  http_path: string;
  client_id: string;
  jdbc_driver_class: string;
  jdbc_url: string;
  jdbc_driver_jar_path: string;
  bridge_label: string;
  copybook_text: string;
  // crm-kind fields (Target Platform: Salesforce / Dynamics 365 / Power
  // Platform / Workday) — blank on source/target rows, same as the DB
  // fields above are blank on crm rows.
  target_type: string;
  instance_url: string;
  consumer_key: string;
  tenant_id: string;
  resource_url: string;
  has_password: boolean;
  has_client_secret: boolean;
  // 2026-09-04, PR review: security_token is no longer returned — it's a
  // credential (half of Salesforce's SOAP login) and is now packed into the
  // row's credential_ref server-side, so only its presence is exposed, the
  // same has_X convention password and client_secret already use. Send a
  // blank security_token on update to mean "leave unchanged".
  has_security_token: boolean;
  // Workday Prism Analytics' OAuth2 refresh_token grant credential — same
  // has_X convention as the other secrets above.
  has_refresh_token: boolean;
  updated_at: string;
}

export interface CrmConnectionOut {
  project_id: string;
  target_type: "salesforce" | "dynamics365" | "power_platform" | "workday";
  instance_url: string;
  username: string;
  consumer_key: string;
  tenant_id: string;
  client_id: string;
  resource_url: string;
  has_password: boolean;
  has_client_secret: boolean;
  // 2026-09-04, PR review: see SavedConnectionOut.has_security_token —
  // the token itself is no longer returned in cleartext.
  has_security_token: boolean;
  has_refresh_token: boolean;
  updated_at: string;
}

export interface MCPTokenView {
  token_id: string;
  consumer_id: string;
  scopes: string[];
  revoked: boolean;
}

export interface MCPTokenIssueResponse {
  token_id: string;
  consumer_id: string;
  scopes: string[];
  secret: string;
}

// ---------------------------------------------------------------------------
// Reports v2 — P10g/h
// ---------------------------------------------------------------------------

export type ReportFragmentKind =
  | "narrative"
  | "metric"
  | "table"
  | "chart"
  | "callout"
  | "section";

export interface ReportFragment {
  skill_name: string;
  kind: ReportFragmentKind;
  title: string;
  order?: number;
  section?: string;
  classification?: string;
  payload?: Record<string, unknown>;
}

export interface ReportContent {
  run_id: string;
  project_id: string;
  title: string;
  branding_mode?: string;
  sections: Record<string, ReportFragment[]>;
  metadata?: Record<string, unknown>;
}

export interface RenderReportRequest {
  content: ReportContent;
  format: "docx" | "pdf" | "pptx";
  overlay_pack?: string | null;
  overlay_version?: string | null;
  use_branding_mode?: boolean;
}

/** 202 response from enqueuing a render job (AC-P10.10). */
export interface EnqueueReportResponse {
  export_id: string;
  status: string;
  format: "docx" | "pdf" | "pptx";
}

export type ReportJobState = "pending" | "generating" | "completed" | "failed";

/** Status of an async render job (GET /api/v2/reports/{id}). */
export interface ReportJobStatus {
  export_id: string;
  status: ReportJobState;
  format: string;
  filename: string;
  mime_type: string;
  ready: boolean;
  byte_length: number | null;
  error_detail: string | null;
  download_url: string | null;
}

interface APIEnvelopeV2<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export const reportsV2 = {
  /**
   * POST /api/v2/reports/render — render + download in a single request.
   * No Celery job, no polling. Prefer this over enqueue/waitForReport/
   * downloadArtifact below — the async job path is proven to hang
   * indefinitely in production, and all three render tools are pure-Python
   * and fast enough to run inline.
   */
  renderAndDownload: async (
    body: RenderReportRequest,
    filenameFallback: string,
  ): Promise<void> => {
    const res = await fetch(`${BASE}/reports/render`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const errBody = await res
        .json()
        .catch(() => ({ detail: res.statusText }));
      throw new Error(
        formatApiErrorDetail(
          errBody.detail,
          `Report render failed: ${res.status}`,
        ),
      );
    }
    const disposition = res.headers.get("Content-Disposition") || "";
    const filename =
      /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? filenameFallback;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /**
   * POST /api/v2/reports — enqueue an async render job (AC-P10.10).
   * Rendering runs in the Celery worker; poll {@link getStatus} for
   * completion, then {@link downloadArtifact} to fetch the bytes.
   * @deprecated prefer {@link renderAndDownload} — this path hangs in
   * production; kept only in case a caller genuinely needs job semantics.
   */
  enqueue: async (
    body: RenderReportRequest,
  ): Promise<EnqueueReportResponse> => {
    const env = await request<APIEnvelopeV2<EnqueueReportResponse>>(
      "/reports",
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    return env.data;
  },

  /** GET /api/v2/reports/{id} — poll a render job's status. */
  getStatus: async (exportId: string): Promise<ReportJobStatus> => {
    const env = await request<APIEnvelopeV2<ReportJobStatus>>(
      `/reports/${exportId}`,
    );
    return env.data;
  },

  /**
   * Poll {@link getStatus} until the job completes (resolves) or fails /
   * times out (rejects). Calls `onStatus` after each poll so callers can
   * surface "generating…" state.
   */
  waitForReport: async (
    exportId: string,
    opts?: {
      intervalMs?: number;
      timeoutMs?: number;
      onStatus?: (s: ReportJobStatus) => void;
    },
  ): Promise<ReportJobStatus> => {
    const intervalMs = opts?.intervalMs ?? 1500;
    const timeoutMs = opts?.timeoutMs ?? 120_000;
    const deadline = Date.now() + timeoutMs;
    for (;;) {
      const status = await reportsV2.getStatus(exportId);
      opts?.onStatus?.(status);
      if (status.status === "completed") return status;
      if (status.status === "failed") {
        throw new Error(status.error_detail || "Report generation failed.");
      }
      if (Date.now() > deadline) {
        throw new Error("Timed out waiting for the report to render.");
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  },

  /**
   * GET /api/v2/reports/{id}/download — stream rendered bytes and trigger a
   * browser download. The endpoint returns raw bytes (not a JSON envelope).
   */
  downloadArtifact: async (
    exportId: string,
    filename: string,
  ): Promise<void> => {
    const res = await fetch(`${BASE}/reports/${exportId}/download`, {
      credentials: "include",
    });
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(
        formatApiErrorDetail(body.detail, `Download failed: ${res.status}`),
      );
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /** GET /api/v2/reports/formats — supported formats + MIME types. */
  listFormats: async (): Promise<
    Array<{ format: string; mime_type: string; extension: string }>
  > => {
    const env =
      await request<
        APIEnvelopeV2<
          Array<{ format: string; mime_type: string; extension: string }>
        >
      >("/reports/formats");
    return env.data;
  },
};

export interface OutputArtifact {
  artifact_id: string;
  tenant_id: string;
  agent: string;
  session_id: string;
  project_id: string | null;
  kind: string;
  label: string;
  filename: string;
  rel_path: string;
  size: number;
  created_at: string;
}
